import React, { useState, useEffect } from 'react';

import API_BASE_URL from"../Config/config";
import {
  Container,
  Box,
  Typography,
  Paper,
  Grid,
  Button,
  Chip,
  CircularProgress,
  Divider,
  Card,
  CardContent,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Alert,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Tooltip,
  IconButton,
  FormLabel,
  RadioGroup,
  Radio,
  Stack,
  InputAdornment,
} from '@mui/material';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { api } from '../context/AuthContext';
import { viewResume } from "../services/fileService";
import {
  Checkbox,
  FormControlLabel,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
} from "@mui/material";
import {
  Assignment,
  Code,
  Psychology,
  Group,
  CheckCircle,
  Cancel,
  Schedule,
  Description,
  Close,
  PersonAdd,
  HighlightOff,
  Settings,
  CalendarMonth,
  ChevronRight,
  ArrowDropDown,
  Badge,
  Info,
  ContentCopy,
  Search,
} from '@mui/icons-material';

function ApplicationDetails() {
  const { id } = useParams();
  const [application, setApplication] = useState(null);
  const [isCodeModalOpen, setIsCodeModalOpen] = useState(false);

  const [codingTest, setCodingTest] = useState(null);
  const [techInterview, setTechInterview] = useState(null);
  const [hrInterview, setHrInterview] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [scheduleDialogOpen, setScheduleDialogOpen] = useState(false);
  const [attemptHistory, setAttemptHistory] = useState([]);
  const [historyDialogOpen, setHistoryDialogOpen] = useState(false);
  const [isReschedule, setIsReschedule] = useState(false);
  const [statusDialogOpen, setStatusDialogOpen] = useState(false);
  const [pendingStatus, setPendingStatus] = useState('');
  const [statusNotes, setStatusNotes] = useState('');
  const [isShortlisting, setIsShortlisting] = useState(false);
  const [interviewToSchedule, setInterviewToSchedule] = useState(''); // 'coding' | 'technical' | 'hr'
  const [technicalInterviewType, setTechnicalInterviewType] = useState(''); // 'AI_TECHNICAL' | 'TECHNICAL_L2'
  const [hrInterviewType, setHrInterviewType] = useState('');              // 'AI_HR_INTERVIEW' | 'HR_L2_INTERVIEW'
  const [scheduledDate, setScheduledDate] = useState('');
  const [level, setLevel] = useState("fresher");
  const [questions, setQuestions] = useState([{
    question: "",
    visibleTestCases: [
      { input: "", expectedOutput: "" },
      { input: "", expectedOutput: "" }
    ]
  }]);

  // Scheduling state
  const [meetingLink, setMeetingLink] = useState('');

  const [interviewMode, setInterviewMode] = useState('Virtual');
  const [codingTestType, setCodingTestType] = useState('AI-Generated');
  const [primarySkills, setPrimarySkills] = useState([]);
  const [skillSearch, setSkillSearch] = useState('');
  const [skillCategory, setSkillCategory] = useState(null);

  const [manualQuestion, setManualQuestion] = useState({
    question: '',
    timeLimit: 30,
    maxScore: 100
  });
  const [manualInterviewQuestions, setManualInterviewQuestions] = useState('');
  const [notes, setNotes] = useState('');

  // Resets ALL scheduling form fields — call this every time a schedule dialog is opened
  const resetFormState = () => {
    setIsReschedule(false);
    setTechnicalInterviewType('');
    setHrInterviewType('');
    setScheduledDate('');
    setMeetingLink('');
    setInterviewMode('Virtual');
    setNotes('');
    setLevel('fresher');
    setPrimarySkills([]);
    setSkillSearch('');
    setSkillCategory((application?.job?.job_type || 'technical') === 'non_technical_bpo' ? 'Non-Technical' : 'Technical');
    setCodingTestType('AI-Generated');
    setManualQuestion({ question: '', timeLimit: 30, maxScore: 100 });
    setQuestions([{
      question: "",
      visibleTestCases: [
        { input: "", expectedOutput: "" },
        { input: "", expectedOutput: "" }
      ]
    }]);

    setManualInterviewQuestions('');
  };

  const [dynamicSkillOptions, setDynamicSkillOptions] = useState([]);

  useEffect(() => {
    const fetchSkills = async () => {
      try {
        const res = await api.get('/technologies');
        if (res.data && Array.isArray(res.data)) {
          const structuredSkills = [];
          res.data.filter(t => t.status === 'Active').forEach(t => {
            const techCategory = t.category || 'Technical';
            structuredSkills.push({ name: t.technology_name, isSub: false, category: techCategory });
            if (t.sub_technologies && Array.isArray(t.sub_technologies)) {
              t.sub_technologies
                .filter(st => st.status === 'Active')
                .forEach(st => {
                  structuredSkills.push({ name: st.sub_technology_name, isSub: true, category: techCategory });
                });
            }
          });
          setDynamicSkillOptions(structuredSkills);
        }
      } catch (err) {
        console.error('Error fetching dynamic skills:', err);
      }
    };
    fetchSkills();
  }, []);

  // Parse primary_skills from any storage format (array, JSON string, CSV, PG literal)
  const parseSkillsFromRaw = (raw) => {
    if (!raw) return [];
    if (Array.isArray(raw)) return raw.map(s => String(s).trim()).filter(Boolean);
    const s = String(raw).trim();
    if (s.startsWith('[')) { try { return JSON.parse(s); } catch (_) {} }
    if (s.startsWith('{')) {
      return s.slice(1, -1).split(',').map(x => x.replace(/^"|"$/g, '').trim()).filter(Boolean);
    }
    return s.split(',').map(x => x.trim()).filter(Boolean);
  };

  // Collect skills already used in previous AI rounds for this candidate
  const usedInPriorRounds = (() => {
    const used = new Set();
    // Scheduling technical AI (Round 2): exclude skills from coding round
    if (interviewToSchedule === 'technical' && technicalInterviewType === 'AI_TECHNICAL') {
      if (codingTest?.primary_skills &&
          (codingTest.coding_test_type === 'AI-Generated' || codingTest.coding_test_type === 'AI-Manual')) {
        parseSkillsFromRaw(codingTest.primary_skills).forEach(s => used.add(s));
      }
    }
    // Scheduling HR AI (Round 3 / BPO Round 2): exclude skills from coding + technical AI rounds
    if (interviewToSchedule === 'hr' && hrInterviewType === 'AI_HR_INTERVIEW') {
      if (codingTest?.primary_skills &&
          (codingTest.coding_test_type === 'AI-Generated' || codingTest.coding_test_type === 'AI-Manual')) {
        parseSkillsFromRaw(codingTest.primary_skills).forEach(s => used.add(s));
      }
      // Only exclude from techInterview if it was an AI_TECHNICAL round (not already overwritten by HR_BEHAVIORAL)
      if (techInterview?.primary_skills && techInterview.technical_interview_type === 'AI_TECHNICAL') {
        parseSkillsFromRaw(techInterview.primary_skills).forEach(s => used.add(s));
      }
    }
    return used;
  })();

  const isHrAiScheduling = interviewToSchedule === 'hr' && hrInterviewType === 'AI_HR_INTERVIEW';
  const currentOptions = dynamicSkillOptions
    .filter(skill => isHrAiScheduling || !usedInPriorRounds.has(skill.name))
    .filter(skill => !skillCategory || skill.category === skillCategory);

  // --- NEW STATE FOR ADMIN NOTES ---
  const [adminNotes, setAdminNotes] = useState('');

  const navigate = useNavigate();
  const [usage, setUsage] = useState({
    used: 0,
    limit: 50,
    remaining: 50,
    plan: ""
  });
  const [overageUsage, setOverageUsage] = useState(null);

  const [showUpgradePopup, setShowUpgradePopup] = useState(false);

  const [selectedInterviews, setSelectedInterviews] = useState([]);
  const [selectionSubmitted, setSelectionSubmitted] = useState(false);


  const [interviewAction, setInterviewAction] = useState('');

  const [decisionDialogOpen, setDecisionDialogOpen] = useState(false);
  const [decisionType, setDecisionType] = useState("");
  const [decisionNotes, setDecisionNotes] = useState("");

  // ── Document Collection state ──
  const [docChecklist, setDocChecklist] = useState({
    govId: true, photo: true, resume: true, educationCerts: true,
    experienceLetters: true, relievingLetter: true, salarySlips: true,
    addressProof: true, bankDetails: true, offerAcceptance: true, bgvConsent: true,
  });
  const [docAdditional, setDocAdditional] = useState('');
  const [docEmail, setDocEmail] = useState('');
  const [docInstructions, setDocInstructions] = useState('');
  const [docDeadline, setDocDeadline] = useState('');
  const [docEmailSent, setDocEmailSent] = useState(false);
  const [docSendingEmail, setDocSendingEmail] = useState(false);
  const [docTotalSent, setDocTotalSent] = useState(0);

  useEffect(() => {
    // 🔥 RESET ALL STATE WHEN APPLICATION CHANGES
    setCodingTest(null);
    setTechInterview(null);
    setHrInterview(null);
    setApplication(null);

    setSelectedInterviews([]);
    setSelectionSubmitted(false);

    setScheduledDate('');
    setMeetingLink('');
    setNotes('');
  }, [id]);

  useEffect(() => {
    async function fetchData() {
      try {
        const appRes = await api.get(`/applications/${id}`);
        console.log("🚀 FETCHED APPLICATION:", appRes.data);
        setApplication(appRes.data);



        // --- PRE-FILL ADMIN NOTES IF THEY EXIST ---
        if (appRes.data.notes) {
          setAdminNotes(appRes.data.notes);
        }

        // ✅ LOAD SAVED INTERVIEW SELECTION
        // Prioritize backend data, fallback to localStorage if needed (for unsaved local state)
        if (appRes.data.selected_interviews || appRes.data.selection_submitted) {
          setSelectedInterviews(appRes.data.selected_interviews || []);
          setSelectionSubmitted(appRes.data.selection_submitted || false);
        } else {
          const saved = localStorage.getItem(`interviewSelection_${id}`);
          if (saved) {
            const parsed = JSON.parse(saved);
            setSelectedInterviews(parsed.selectedInterviews || []);
            setSelectionSubmitted(parsed.selectionSubmitted || false);
          }
        }

        // Try fetching associated assessment data
        try {
          const [ctRes, tiRes, hrRes] = await Promise.allSettled([
            api.get(`/assessments/coding-test/${id}`),
            api.get(`/assessments/technical-interview/${id}`),
            api.get(`/assessments/hr-interview/${id}`),
          ]);

          if (ctRes.status === 'fulfilled') setCodingTest(ctRes.value.data);
          if (tiRes.status === 'fulfilled') setTechInterview(tiRes.value.data);
          if (hrRes.status === 'fulfilled') setHrInterview(hrRes.value.data);
        } catch (err) {
          console.warn('Failed to fetch some assessment details', err);
        }

        fetchAttemptHistory();
      } catch (error) {
        console.error('Failed to fetch application details:', error);
        setError('Failed to load application details.');
      } finally {
        setLoading(false);
      }
    }
    fetchData();
  }, [id]);

  useEffect(() => {
    const interval = setInterval(async () => {
      try {
        const appRes = await api.get(`/applications/${id}`);
        if (appRes.data.status !== application?.status) {
          setApplication(appRes.data);
        }

        // ✅ Also sync interview level selection from backend
        // ONLY sync if backend says it's submitted, to avoid overwriting local unsaved checkboxes
        if (appRes.data.selection_submitted && !selectionSubmitted) {
            setSelectedInterviews(appRes.data.selected_interviews || []);
            setSelectionSubmitted(true);
        }

        const [ctRes, tiRes, hrRes] = await Promise.allSettled([
          api.get(`/assessments/coding-test/${id}`),
          api.get(`/assessments/technical-interview/${id}`),
          api.get(`/assessments/hr-interview/${id}`),
        ]);

        // ✅ coding test
        if (
          ctRes.status === 'fulfilled' &&
          ctRes.value.data &&
          ctRes.value.data.application_id === id
        ) {
          setCodingTest(ctRes.value.data);
        }

        // ✅ technical interview
        if (
          tiRes.status === 'fulfilled' &&
          tiRes.value.data &&
          tiRes.value.data.application_id === id
        ) {
          setTechInterview(tiRes.value.data);
        }

        // ✅ hr interview
        if (
          hrRes.status === 'fulfilled' &&
          hrRes.value.data &&
          hrRes.value.data.application_id === id
        ) {
          setHrInterview(hrRes.value.data);
        }
      } catch (err) {
        console.error("Auto refresh failed", err);
      }
    }, 5000); // every 5 sec

    return () => clearInterval(interval);
  }, [id, selectionSubmitted, application?.status]);

  const handleUpdateStatus = async (overrideStatus = null) => {
    try {
      const finalStatus = overrideStatus || pendingStatus;

      const response = await api.patch(`/admin/applications/${id}`, {
        status: finalStatus,
        notes: statusNotes
      });

      setApplication(response.data);

      // ✅ Refresh assessment data to reflect cleared scheduled flags
      try {
        const [ctRes, tiRes, hrRes] = await Promise.allSettled([
          api.get(`/assessments/coding-test/${id}`),
          api.get(`/assessments/technical-interview/${id}`),
          api.get(`/assessments/hr-interview/${id}`),
        ]);

        if (ctRes.status === 'fulfilled') setCodingTest(ctRes.value.data);
        if (tiRes.status === 'fulfilled') setTechInterview(tiRes.value.data);
        if (hrRes.status === 'fulfilled') setHrInterview(hrRes.value.data);
      } catch (err) {
        console.warn('Failed to refresh assessment details', err);
      }

      setSuccess(`Application marked as ${formatStatus(finalStatus)}`);
      setStatusDialogOpen(false);
      setStatusNotes('');

    } catch (err) {
      setError(
        formatStatus(
          err.response?.data?.message || 'Failed to update status'
        )
      );
    }
  };
  // ✅ FETCH USAGE
  const fetchUsage = async () => {
    try {
      const res = await api.get('/applications/starter-usage');
      console.log("🚀 STARTER USAGE:", res.data);
      setUsage(res.data);
      
      // Also fetch overage usage if on monthly plan
      if (res.data.plan === "monthly") {
        const overageRes = await api.get('/applications/overage-usage');
        console.log("🚀 OVERAGE USAGE:", overageRes.data);
        setOverageUsage(overageRes.data);
      }
    } catch (err) {
      console.error("Usage fetch failed", err);
    }
  };

  const fetchAttemptHistory = async () => {
    try {
      const res = await api.get(`/assessments/interview-attempts/${id}`);
      setAttemptHistory(res.data || []);
    } catch (err) {
      console.error("Attempt history fetch failed", err);
    }
  };

  // --- NEW HANDLER FOR SAVING ADMIN NOTES ---
  const handleSaveAdminNotes = async () => {
    try {
      // Hits the new route that doesn't trigger emails
      const response = await api.put(`/admin/applications/${id}/notes`, {
        notes: adminNotes
      });

      // Update local state with the saved data
      setApplication(prev => ({ ...prev, notes: response.data.notes }));
      setSuccess('Notes saved successfully');
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to save private notes');
    }
  };

  const handleScheduleInterview = async () => {
    try {
      let endpoint = '';
      let payload = {
        scheduledAt: new Date(scheduledDate).toISOString(),
        meetingLink: (interviewToSchedule !== 'coding' && interviewMode === 'Virtual') ? meetingLink : '',
        interviewMode,
        notes: (interviewToSchedule !== 'coding' && interviewMode === 'Virtual') ? '' : notes
      };

      if (interviewToSchedule === 'technical') {
        endpoint = `/assessments/technical-interview/${id}/schedule`;
        payload.technicalInterviewType = technicalInterviewType;

        if (technicalInterviewType === 'AI_TECHNICAL') {
          payload.primarySkills = primarySkills;
          payload.level = level;
          payload.timeLimit = manualQuestion.timeLimit;
          payload.interviewMode = 'Virtual'; // AI Technical is always Virtual
        } else {
          // TECHNICAL_L2
          if (manualInterviewQuestions) {
            payload.questions = manualInterviewQuestions.split('\n').filter(q => q.trim() !== '');
          }
        }
      } else if (interviewToSchedule === 'hr') {
        if (hrInterviewType === 'AI_HR_INTERVIEW') {
          // AI HR Interview is handled by the technical-interview endpoint with HR_BEHAVIORAL type
          endpoint = `/assessments/technical-interview/${id}/schedule`;
          payload.technicalInterviewType = 'HR_BEHAVIORAL';
          payload.primarySkills = primarySkills;
          payload.level = level;
          payload.timeLimit = manualQuestion.timeLimit;
          payload.interviewMode = 'Virtual';
          payload.meetingLink = '';
        } else {
          // HR L2 Interview — traditional in-person or virtual with recruiter
          endpoint = `/assessments/hr-interview/${id}/schedule`;
          if (manualInterviewQuestions) {
            payload.questions = manualInterviewQuestions.split('\n').filter(q => q.trim() !== '');
          }
        }
      } else if (interviewToSchedule === 'coding') {
        endpoint = `/assessments/coding-test/${id}/schedule`;
        payload.codingTestType = codingTestType;
        console.log("🚀 SENDING LEVEL TO BACKEND:", level);
        payload.level = level;

        if (codingTestType === 'AI-Generated') {
          payload.primarySkills = primarySkills;

          // ✅ SEND TIME LIMIT
          payload.timeLimit = manualQuestion.timeLimit;

          // ✅ OPTIONAL DEFAULT SCORE
          payload.maxScore = manualQuestion.maxScore || 100;
        }

        if (codingTestType === 'AI-Manual') {
          payload.primarySkills = primarySkills;
          const validQuestions = questions.filter(q => q && q.question && q.question.trim() !== "");

          payload.coding_questions = validQuestions;   // ✅ ALL QUESTIONS WITH TEST CASES

          payload.manualQuestions = manualInterviewQuestions
            .split('\n')
            .map(q => q.trim())
            .filter(q => q !== '');

          payload.timeLimit = manualQuestion.timeLimit;
          payload.maxScore = manualQuestion.maxScore;
        }
      }
      console.log("🚀 FINAL PAYLOAD:", payload);

      const response = await api.patch(endpoint, payload);

      // ✅ FORCE REFRESH APPLICATION + ALL STATUSES
      const appRes = await api.get(`/applications/${id}`);
      setApplication(appRes.data);

      const [ctRes, tiRes, hrRes] = await Promise.allSettled([
        api.get(`/assessments/coding-test/${id}`),
        api.get(`/assessments/technical-interview/${id}`),
        api.get(`/assessments/hr-interview/${id}`),
      ]);

      if (ctRes.status === 'fulfilled') setCodingTest(ctRes.value.data);
      if (tiRes.status === 'fulfilled') setTechInterview(tiRes.value.data);
      if (hrRes.status === 'fulfilled') setHrInterview(hrRes.value.data);

      if (interviewToSchedule === 'technical') {
        setTechInterview(response.data);
      } else if (interviewToSchedule === 'hr') {
        setHrInterview(response.data);
      } else if (interviewToSchedule === 'coding') {
        setCodingTest(response.data);
      }

      let typeLabel = '';

      if (interviewToSchedule === 'coding') {
        typeLabel = 'Coding Test';
      }
      else if (interviewToSchedule === 'technical') {

        if (application?.job?.job_type === 'non_technical_bpo') {
          typeLabel =
              technicalInterviewType === 'TECHNICAL_L2'
                  ? 'Customer Support L2 Interview'
                  : 'AI Communication & Customer Support Assessment';
        } else {
          typeLabel =
              technicalInterviewType === 'TECHNICAL_L2'
                  ? 'Technical L2 Interview'
                  : 'Technical Interview';
        }

      }
      else {

        if (application?.job?.job_type === 'non_technical_bpo') {
          typeLabel =
              hrInterviewType === 'HR_L2_INTERVIEW'
                  ? 'Operations & HR Evaluation L2 Interview'
                  : 'Operations & HR Evaluation';
        } else {
          typeLabel =
              hrInterviewType === 'HR_L2_INTERVIEW'
                  ? 'HR L2 Interview'
                  : 'HR Interview';
        }

      }

      if (response.data?.warning) {
        setError(response.data.warning);
      } else {
        setSuccess(
            `${typeLabel} ${isReschedule ? 'rescheduled' : 'scheduled'} successfully`
        );
      }
      fetchAttemptHistory();
      setScheduleDialogOpen(false);
      setScheduledDate('');
      setMeetingLink('');
      setNotes('');
      setInterviewMode('Virtual');
      setManualInterviewQuestions('');
    } catch (err) {
      setError(
        formatStatus(
          err.response?.data?.message || 'Failed to schedule interview'
        )
      );
    }
  };

  const handleInterviewAction = (type, action) => {
    console.log(`handleInterviewAction triggered: type=${type}, action=${action}`);
    if (action === "schedule") {
      resetFormState();
      setInterviewToSchedule(type);
      setScheduleDialogOpen(true);
    }

    if (action === "cancel") {
      const isL2 = type === 'technical'
        ? (techInterview?.technicalInterviewType !== 'AI_TECHNICAL' && techInterview?.technical_interview_type !== 'AI_TECHNICAL')
        : !(!!(hrInterview?.interview_token || hrInterview?.interviewToken) || ['ai_hr_interview_scheduled', 'ai_hr_interview_completed'].includes(application?.status));

      const status =
        type === "technical"
          ? (isL2 ? "technical_l2_interview_cancelled" : "technical_cancelled")
          : (isL2 ? "hr_cancelled" : "hr_cancelled");

      setPendingStatus(status);
      setInterviewToSchedule(type);
      setStatusDialogOpen(true);
    }

    if (action === "completed") {
      const isL2 = type === 'technical'
        ? (techInterview?.technicalInterviewType !== 'AI_TECHNICAL' && techInterview?.technical_interview_type !== 'AI_TECHNICAL')
        : !(!!(hrInterview?.interview_token || hrInterview?.interviewToken) || ['ai_hr_interview_scheduled', 'ai_hr_interview_completed'].includes(application?.status));

      const status =
        type === "technical"
          ? (isL2 ? "technical_l2_interview_completed" : "technical_completed")
          : (isL2 ? "hr_l2_interview_completed" : "hr_completed");

      handleUpdateStatus(status); // ✅ DIRECT PASS
    }
  };

  const handleOpenDecisionDialog = (type) => {
    setDecisionType(type);

    if (type === "selected") {
      setDecisionNotes("Congratulations! You have been selected.\n\n[Please customize this message]");
    } else {
      setDecisionNotes("We regret to inform you that you were not selected.");
    }

    setDecisionDialogOpen(true);
  };

  const handleCloseDecisionDialog = () => {
    setDecisionDialogOpen(false);
    setDecisionNotes("");
    setDecisionType("");
  };
  // ✅ LOAD USAGE
  useEffect(() => {
    fetchUsage();
  }, []);


  if (loading) {
    return (
        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 8 }}>
          <CircularProgress />
        </Box>
    );
  }

  const getCodingStatus = () => {
    if (application?.status === "terminated") return "Terminated";
    if (codingTest?.completed_at && codingTest?.score != null) return "Completed";
    if (codingTest?.scheduled_at) return "Scheduled";
    return "Pending";
  };
  const formatDateTime = (date) => {
    if (!date || date === 'null' || date === 'undefined') return '';

    try {
      const d = new Date(date);
      if (isNaN(d.getTime())) return '';

      const options = {
        day: "numeric",
        month: "short",
        year: "numeric",
        hour: "2-digit",
        minute: "2-digit",
        hour12: true,
      };
      return d.toLocaleString("en-GB", options).replace(',', '');
    } catch (e) {
      return '';
    }
  };

  const getStatusLabel = (status) => {
    // All labels are derived from INTERVIEW_CONFIG so they match the
    // configured interview level name for every job type automatically.
    const techLabel   = INTERVIEW_CONFIG?.labels?.technical || 'Technical Interview';
    const hrLabel     = INTERVIEW_CONFIG?.labels?.hr        || 'HR Interview';
    const codingLabel = INTERVIEW_CONFIG?.labels?.coding    || 'AI Coding Assessment';
    const techL2Label = isBPO
        ? 'Customer Support L2 Interview'
        : `${techLabel} L2 Interview`;

    const hrL2Label = isBPO
        ? 'Operations & HR Evaluation L2 Interview'
        : `${hrLabel} L2 Interview`;
    const map = {
      ai_coding_scheduled:             `${codingLabel} Scheduled`,
      ai_coding_completed:             `${codingLabel} Completed`,
      terminated:                       "Interview Terminated",
      application_shortlisted:          "Shortlisted",
      technical_scheduled:             `${techLabel} Scheduled`,
      technical_completed:             `${techLabel} Completed`,
      technical_cancelled:             `${techLabel} Cancelled`,
      technical_interview_completed:   `${techLabel} Completed`,
      hr_scheduled:                    `${hrLabel} Scheduled`,
      hr_completed:                    `${hrLabel} Completed`,
      hr_cancelled:                    `${hrLabel} Cancelled`,
      hr_interview_completed:          `${hrLabel} Completed`,
      ai_technical_interview_scheduled:`${techLabel} Scheduled`,
      ai_technical_interview_completed:`${techLabel} Completed`,
      technical_l2_interview_scheduled:`${techL2Label} Scheduled`,
      TECHNICAL_L2_SCHEDULED:          `${techL2Label} Scheduled`,
      technical_l2_interview_completed:`${techL2Label} Completed`,
      TECHNICAL_L2_COMPLETED:          `${techL2Label} Completed`,
      TECHNICAL_L2_CANCELLED:          `${techL2Label} Cancelled`,
      technical_l2_interview_cancelled:`${techL2Label} Cancelled`,
      ai_hr_interview_scheduled:       `${hrLabel} Scheduled`,
      ai_hr_interview_completed:       `${hrLabel} Completed`,
      hr_l2_interview_scheduled:       `${hrL2Label} Scheduled`,
      hr_l2_interview_completed:       `${hrL2Label} Completed`,
      hr_l2_interview_cancelled:       `${hrL2Label} Cancelled`,
      HR_L2_SCHEDULED:                 `${hrL2Label} Scheduled`,
      HR_L2_COMPLETED:                 `${hrL2Label} Completed`,
      HR_L2_CANCELLED:                 `${hrL2Label} Cancelled`,
      candidate_selected:               "Selected",
      candidate_rejected:               "Rejected",
      offer_released:                   "Offer Released",
    };

    if (!status) return "Pending Review";
    return map[status] || status;
  };

  const getStatusColor = (status) => {
    const colors = {
      application_shortlisted: "success",
      technical_scheduled: "info",
      hr_scheduled: "info",
      candidate_selected: "success",
      candidate_rejected: "error",
      offer_released: "success",
      terminated: "error",
    };

    return colors[status] || "warning";
  };

  const formatStatus = (status) => {
    if (!status) return "";

    return status
      .replace(/_/g, " ")
      .replace(/\s+/g, " ")
      .trim()
      .replace(/\bhr\b/gi, "HR")
      .replace(/\bai\b/gi, "AI")
      .replace(/\b\w/g, c => c.toUpperCase());
  };
  const handleViewFile = (fileUrl) => {
    if (!fileUrl) return;
    window.open(fileUrl, '_blank');
  };

  if (!application) {
    return (
        <Container sx={{ py: 4 }}>
          <Alert severity="error">Application not found</Alert>
        </Container>
    );
  }

  const isShortlisted =
    application?.status === "application_shortlisted" ||
    [
    "ai_coding_scheduled", "ai_coding_completed",
    "terminated",
    "application_shortlisted",
    "technical_scheduled", "technical_cancelled", "technical_completed", "technical_interview_completed",
    "hr_scheduled", "hr_cancelled", "hr_completed", "hr_interview_completed",
    "candidate_selected", "offer_released",
    // Type-specific statuses
    "ai_technical_interview_scheduled", "ai_technical_interview_completed",
    "technical_l2_interview_scheduled", "technical_l2_interview_completed",
    "TECHNICAL_L2_SCHEDULED", "TECHNICAL_L2_COMPLETED", "TECHNICAL_L2_CANCELLED",
    "ai_hr_interview_scheduled", "ai_hr_interview_completed",
    "hr_l2_interview_scheduled", "hr_l2_interview_completed",
  ].includes(application?.status);

  const isSelected =
    application?.status === "candidate_selected";

  const isRejected =
    application?.status === "candidate_rejected";

  const isOffered =
    application?.status === "offer_released";

  const isTerminated =
    application?.status === 'terminated' || application?.pipeline_locked === true;

  const isLocked =
    application?.status === "offer_released" || isRejected || isTerminated; // 🔥 FINAL LOCK

  // ── Configuration-driven interview flow ────────────────────────────────────
  // `job_type` is stored on the job when it is posted. Default to 'technical'.
  const isBPO = (application?.job?.job_type || 'technical') === 'non_technical_bpo';

  const INTERVIEW_CONFIG = isBPO
    ? {
        availableRounds: ['technical', 'hr'],
        roundNumber: { technical: 1, hr: 2 },
        labels: {
          technical: 'AI Communication & Customer Support Assessment',
          hr: 'Operations & HR Evaluation',
        },
        descriptions: {
          technical:
            'Evaluate communication skills, listening ability, customer handling, English proficiency, and role-specific aptitude using AI.',
          hr: 'Assess process understanding, customer service scenarios, culture fit, shift flexibility, and overall suitability for the role.',
        },
      }
    : {
        availableRounds: ['coding', 'technical', 'hr'],
        roundNumber: { coding: 1, technical: 2, hr: 3 },
        labels: {
          coding: 'AI Technical Assessment',
          technical: 'Technical Evaluation Round',
          hr: 'HR & Behavioral Evaluation',
        },
        descriptions: {
          coding: 'Evaluate coding skills and problem-solving abilities using AI-powered assessment.',
          technical: 'Assess technical expertise, system design, and core problem-solving skills.',
          hr: 'Evaluate communication skills, cultural fit, and overall personality.',
        },
      };
  // ───────────────────────────────────────────────────────────────────────────
  // Shared L2 labels derived from the configured interview level names
  const techL2Label = isBPO
      ? 'Customer Support L2 Interview'
      : `${INTERVIEW_CONFIG.labels.technical} L2 Interview`;

  const hrL2Label = isBPO
      ? 'Operations & HR Evaluation L2 Interview'
      : `${INTERVIEW_CONFIG.labels.hr} L2 Interview`;

    const screeningQuestions = Array.isArray(application?.job?.screening_questions)
      ? application.job.screening_questions.filter((q) => String(q).trim())
      : [];

    let screeningAnswers = {};
    try {
      screeningAnswers =
        typeof application?.screening_answers === 'string'
          ? JSON.parse(application.screening_answers)
          : (application?.screening_answers || {});
    } catch (e) {
      screeningAnswers = {};
    }

  const handleCopyPublicLink = (token) => {
    if (!token) return;
    const url = `${window.location.origin}/interview-status/${token}`;
    navigator.clipboard.writeText(url);
    setSuccess('Public tracking link copied to clipboard!');
    setTimeout(() => setSuccess(''), 3000);
  };

  const renderPublicTrackingBox = (token) => {
    if (!token) return null;
    return (
      <Box sx={{ 
        mt: 2, 
        p: 1.5, 
        backgroundColor: '#f1f5f9', 
        borderRadius: 1, 
        display: 'flex', 
        alignItems: 'center', 
        justifyContent: 'space-between',
        border: '1px dashed #cbd5e1'
      }}>
        <Box>
          <Typography variant="caption" sx={{ color: '#64748b', display: 'block', fontWeight: 600 }}>
            Public Tracking Link
          </Typography>
          <Typography variant="caption" sx={{ color: '#0f172a', fontFamily: 'monospace' }}>
            {`${window.location.origin.substring(0, 20)}.../${token.substring(0, 8)}`}
          </Typography>
        </Box>
        <Tooltip title="Copy Tracking Link">
          <IconButton size="small" onClick={() => handleCopyPublicLink(token)}>
            <ContentCopy fontSize="small" />
          </IconButton>
        </Tooltip>
      </Box>
    );
  };

  return (
    <>
      <Container sx={{ py: 4, minHeight: '100vh' }}>
        {success && <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess('')}>{success}</Alert>}
        {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>{error}</Alert>}

        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 4, flexDirection: { xs: 'column', sm: 'row' }, gap: 2, flexWrap: 'wrap' }}>
          <Box sx={{ minWidth: 0, flex: 1 }}>
            <Typography
                variant="h4"
                gutterBottom
                sx={{
                  wordBreak: "break-word",
                  fontWeight: 700,
                  color: "#111827"
                }}
            >
              {application.candidate?.firstName} {application.candidate?.lastName}
            </Typography>
            <Typography
                sx={{
                  fontSize: "1rem",
                  fontWeight: 600,
                  color: "#2563eb",
                  mb: 0.5
                }}
            >
              Applying for: {application.job?.title}
            </Typography>
            <Typography
                sx={{
                  wordBreak: "break-word",
                  fontSize: "0.95rem",
                  color: "#6b7280"
                }}
            >
              {application.candidate?.email} | Applied on {new Date(application.createdAt).toLocaleDateString()}
            </Typography>
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
            <Chip
              label={getStatusLabel(application?.status)}
              color={getStatusColor(application?.status)}
              size="medium"
              sx={{
                fontWeight: 500,
                borderRadius: "8px",
                textTransform: "capitalize"
              }}
            />

            {/* 🔹 SHORTLIST (ONE TIME ONLY) */}
            <Button
              variant="contained"
              color="success"
              startIcon={<CheckCircle />}
              disabled={isShortlisted || isLocked || isShortlisting}
              onClick={async () => {
                try {
                    setIsShortlisting(true);
                  await api.patch(`/admin/applications/${id}`, {
                    status: "application_shortlisted",
                    notes: "Shortlisted"
                  });

                  setSuccess("Candidate shortlisted successfully");

                   await fetchUsage();  // ✅ update count

                  const res = await api.get(`/applications/${id}`);
                  setApplication(res.data);

                } catch (err) {
                  if (err.response?.status === 403 && usage.plan === "monthly") {
                    setShowUpgradePopup(true); // ✅ popup
                  } else {
                    setError(err.response?.data?.message || "Failed to shortlist");
                  }
                }finally {
                       setIsShortlisting(false);  // ✅ RE-ENABLE (optional)
                     }
              }}
            >
              Shortlist
            </Button>

            {/* 🔹 REJECT (ALWAYS ENABLED) */}
            <Button
              variant="outlined"
              color="error"
              startIcon={<Cancel />}
              disabled={isLocked || isRejected}
              onClick={() => handleOpenDecisionDialog("rejected")}
            >
              Reject
            </Button>

            {/* 🔹 EXISTING */}
            <Tooltip
              title={!isSelected ? "Select the candidate first to enable document collection, document request email sending, and offer release." : ""}
              arrow
              componentsProps={{
                tooltip: {
                  sx: {
                    bgcolor: "#2e7d32",   // ✅ professional green
                    fontSize: "13px",
                    fontWeight: 500
                  }
                },
                arrow: {
                  sx: {
                    color: "#2e7d32"
                  }
                }
              }}
            >
              <span>
                <Button
                  variant="contained"
                  color="primary"
                  startIcon={<Assignment />}
                  disabled={!isSelected}
                  onClick={() => navigate(`/admin/applications/${id}/release-offer`)}
                >
                  Release Offer
                </Button>
              </span>
            </Tooltip>



          </Box>
        </Box>

        <Grid container spacing={4}>
          <Grid item xs={12}>
            <Paper
                sx={{
                  p: 4,
                  mb: 4,
                  borderRadius: 3,
                  border: "1px solid #e5e7eb",
                  boxShadow: "0 8px 24px rgba(15,23,42,0.06)"
                }}
            >
              <Grid container spacing={3} sx={{ mb: 3 }}>

                <Grid item xs={12} md={6}>
                  <Paper
                      variant="outlined"
                      sx={{
                        p: 3,
                        height: "100%",
                        borderRadius: 2
                      }}
                  >
                    <Typography
                        sx={{
                          fontSize: "1.05rem",
                          fontWeight: 700,
                          mb: 2,
                          color: "#111827"
                        }}
                    >
                      Education
                    </Typography>

                    <Typography
                        sx={{
                          fontSize: "0.95rem",
                          lineHeight: 1.8,
                          color: "#4b5563",
                          whiteSpace: "pre-line"
                        }}
                    >
                      {application.education || "No education details provided."}
                    </Typography>
                  </Paper>
                </Grid>

                <Grid item xs={12} md={6}>
                  <Paper
                      variant="outlined"
                      sx={{
                        p: 3,
                        height: "100%",
                        borderRadius: 2
                      }}
                  >
                    <Typography
                        sx={{
                          fontSize: "1.05rem",
                          fontWeight: 700,
                          mb: 2,
                          color: "#111827"
                        }}
                    >
                      Previous Experience
                    </Typography>

                    <Typography
                        sx={{
                          fontSize: "0.95rem",
                          lineHeight: 1.8,
                          color: "#4b5563",
                          whiteSpace: "pre-line"
                        }}
                    >
                      {application.experience || "No experience details provided."}
                    </Typography>
                  </Paper>
                </Grid>

              </Grid>

              <Typography
                  sx={{
                    fontSize: "1.1rem",
                    fontWeight: 700,
                    color: "#111827",
                    mb: 2
                  }}
              >
                Resume / Profile
              </Typography>
              <Box
                  sx={{
                    mt: 2,
                    p: 3,
                    border: "1px solid #e5e7eb",
                    borderRadius: 2,
                    backgroundColor: "#f8fafc",
                    mb: 3
                  }}
              >
                {console.log("RESUME KEY:", application.resume_key)}
                {application.resume_key ? (
                    <Button
                        variant="contained"
                        startIcon={<Description />}
                        onClick={() => viewResume(application.resume_key)}
                        sx={{
                          borderRadius: "10px",
                          textTransform: "none",
                          px: 3,
                          py: 1,
                          fontWeight: 600
                        }}
                    >
                      View Resume
                    </Button>
                ) : (
                  <p>No Resume Available</p>
                )}
              </Box>
            </Paper>
            {screeningQuestions.length > 0 && (
              <Paper sx={{ p: 3, mb: 3 }}>
                <Typography
                  variant="h6"
                  gutterBottom
                  borderBottom={1}
                  borderColor="divider"
                  pb={1}
                >
                  Screening Questions & Answers
                </Typography>

                {screeningQuestions.map((q, index) => {
                  const answer =
                    screeningAnswers[q.key] ??
                    screeningAnswers[q.question] ??
                    'No answer provided';

                  return (
                    <Box key={q.key || index} sx={{ mb: 3 }}>
                      <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 1 }}>
                        {index + 1}. {q.question || q.label || `Question ${index + 1}`}
                      </Typography>

                      <Typography
                        variant="body1"
                        sx={{
                          whiteSpace: 'pre-line',
                          wordBreak: 'break-word',
                          bgcolor: '#f9f9f9',
                          p: 2,
                          borderRadius: 1
                        }}
                      >
                        {String(answer)}
                      </Typography>
                    </Box>
                  );
                })}
              </Paper>
            )}

            {/* INTERVIEW LEVEL SELECTION */}
            <Paper
                sx={{
                  p: 4,
                  mb: 4,
                  borderRadius: 3,
                  border: "1px solid #e5e7eb",
                  boxShadow: "0 8px 24px rgba(15,23,42,0.06)"
                }}
            >
              <Typography
                  sx={{
                    fontSize: "1.4rem",
                    fontWeight: 700,
                    color: "#111827",
                    mb: 2
                  }}
              >
                Select Interview Levels
              </Typography>
              {application?.status !== 'application_shortlisted' && (
                <Typography variant="body2" color="textSecondary" sx={{ mb: 2 }}>
                  To conduct interviews, you must shortlist the candidate first.
                </Typography>
              )}

            {!selectionSubmitted ? (
                <>
                  <FormControl component="fieldset" fullWidth disabled={application?.status !== 'application_shortlisted'}>
                    <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
                      {INTERVIEW_CONFIG.availableRounds.map((roundKey) => (
                        <FormControlLabel
                          key={roundKey}
                          control={
                            <Checkbox
                              checked={selectedInterviews.includes(roundKey)}
                              disabled={application?.status !== 'application_shortlisted'}
                              onChange={(e) => {
                                if (selectionSubmitted) {
                                  alert("Selection level is locked");
                                  return;
                                }
                                if (e.target.checked) {
                                  setSelectedInterviews([...selectedInterviews, roundKey]);
                                } else {
                                  setSelectedInterviews(selectedInterviews.filter(i => i !== roundKey));
                                }
                              }}
                            />
                          }
                          label={INTERVIEW_CONFIG.labels[roundKey]}
                        />
                      ))}
                    </Box>
                  </FormControl>

                {selectedInterviews.length > 0 && (
                  <Typography variant="body2" color="error" sx={{ mt: 2 }}>
                    ⚠️ Once Submitted, Interview Levels Cannot Be Changed.
                  </Typography>
                )}

              {/* BUTTON FIXED POSITION */}
              <Box sx={{ mt: 2 }}>
                <Button
                  variant="contained"
                  disabled={selectedInterviews.length === 0 || application?.status !== 'application_shortlisted'}
                  onClick={async () => {
                    try {
                      setLoading(true);
                      await api.patch(`/applications/${id}`, {
                        selected_interviews: selectedInterviews,
                        selection_submitted: true
                      });
                      
                      setSelectionSubmitted(true);
                      localStorage.setItem(`interviewSelection_${id}`, JSON.stringify({
                        selectedInterviews,
                        selectionSubmitted: true
                      }));
                    } catch (err) {
                      console.error('Failed to save interview selection:', err);
                      alert('Failed to save interview selection. Please try again.');
                    } finally {
                      setLoading(false);
                    }
                  }}
                >
                  Submit
                </Button>
              </Box>
            </>
          ) : (
            <Alert severity="info">
              Interview levels locked. You cannot change them.
            </Alert>
          )}
        </Paper>

           {selectionSubmitted && (
             <>

               <Typography
                   sx={{
                     mt: 5,
                     mb: 3,
                     fontSize: "1.5rem",
                     fontWeight: 700,
                     color: "#111827"
                   }}
               >
                 Interview Progress
               </Typography>

            {isTerminated && (
              <Alert
                severity="warning"
                sx={{
                  mb: 3,
                  borderRadius: 2,
                  border: '1px solid #f59e0b',
                  bgcolor: '#fffbeb',
                  '& .MuiAlert-icon': { color: '#d97706' },
                }}
              >
                <Typography variant="body2" fontWeight="bold" sx={{ color: '#92400e', mb: 0.5 }}>
                  Interview was terminated.
                </Typography>
                <Typography variant="body2" sx={{ color: '#92400e' }}>
                  The interview was terminated due to security violations or browser refresh/close. Remaining interview levels are locked automatically.
                </Typography>
              </Alert>
            )}

            {selectedInterviews.includes("coding") && (
              <Card sx={{
                mb: 3,
                borderRadius: 3,
                boxShadow: "0 4px 20px rgba(0,0,0,0.05)",
                border: "1px solid #f0f0f0",
                position: 'relative',
                '&::before': {
                  content: '""',
                  position: 'absolute',
                  left: 0,
                  top: 0,
                  bottom: 0,
                  width: '4px',
                  backgroundColor: '#1e3a8a',
                  borderRadius: '3px 0 0 3px'
                }
              }}>
                <CardContent sx={{ p: 3 }}>
                  <Box sx={{ display: 'flex', alignItems: 'flex-start', mb: 1 }}>
                    <Box sx={{
                      width: 48,
                      height: 48,
                      borderRadius: 2,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      backgroundColor: '#EFF6FF',
                      mr: 2,
                      flexShrink: 0
                    }}>
                      <Code sx={{ color: '#1e3a8a' }} />
                    </Box>
                    <Box sx={{ flexGrow: 1 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', mb: 0.5 }}>
                        <Box sx={{
                          width: 24,
                          height: 24,
                          borderRadius: '50%',
                          backgroundColor: '#1e3a8a',
                          color: 'white',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          fontSize: '0.75rem',
                          fontWeight: 'bold',
                          mr: 1.5
                        }}>
                          1
                        </Box>
                        <Typography variant="h6" sx={{ fontWeight: 700, color: '#1a1a1a', fontSize: '1.1rem' }}>
                          {INTERVIEW_CONFIG.labels['coding']}
                        </Typography>
                      </Box>
                      <Typography variant="body2" sx={{ color: '#666', mb: 2, ml: 4.5 }}>
                        {INTERVIEW_CONFIG.descriptions['coding']}
                      </Typography>

                      <Box sx={{ ml: 4.5 }}>
                        {codingTest?.completed_at && codingTest?.score != null ? (
                          <Box sx={{
                            backgroundColor: '#f8fafc',
                            p: 2,
                            borderRadius: 2,
                            border: '1px solid #e2e8f0'
                          }}>
                            <Typography variant="body2" sx={{ color: '#475569', mb: 0.5 }}>
                              Score: <strong style={{ color: '#1a1a1a' }}>{codingTest.score} / {codingTest.max_score || codingTest.maxScore}</strong>
                            </Typography>
                            <Typography variant="body2" sx={{ color: '#475569', mb: 1.5 }}>
                              Completed at: {new Date(codingTest.completed_at).toLocaleString()}
                            </Typography>
                            {codingTest.candidate_code && (
                            <Button
                              variant="outlined"
                              size="small"
                              onClick={() => setIsCodeModalOpen(true)}
                              sx={{
                                textTransform: 'none',
                                borderRadius: '8px',
                                fontWeight: 600
                              }}
                            >
                              View Submitted Code
                            </Button>
                            )}
                            {!isTerminated && (
                              <Box sx={{ display: 'flex', gap: 1.5, mt: 1.5, alignItems: 'center' }}>
                                <Button
                                  variant="outlined"
                                  size="small"
                                  startIcon={<CalendarMonth sx={{ fontSize: 16 }} />}
                                  onClick={() => {
                                    resetFormState();
                                    setIsReschedule(true);
                                    setInterviewToSchedule('coding');
                                    setScheduleDialogOpen(true);
                                  }}
                                  sx={{ textTransform: 'none', borderRadius: '8px', fontWeight: 600, borderColor: '#1e3a8a', color: '#1e3a8a' }}
                                >
                                  Reschedule
                                </Button>
                                <Button
                                  variant="text"
                                  size="small"
                                  onClick={() => { fetchAttemptHistory(); setHistoryDialogOpen(true); }}
                                  sx={{ textTransform: 'none', color: '#64748b', fontSize: '0.75rem' }}
                                >
                                  View History
                                </Button>
                              </Box>
                            )}
                          </Box>
                        ) : (codingTest && codingTest.application_id === id && formatDateTime(codingTest.scheduled_at)) ? (
                          <Box>
                            <Box sx={{
                              backgroundColor: '#f8fafc',
                              p: 2,
                              borderRadius: 2,
                              border: '1px solid #e2e8f0',
                              mb: 1.5
                            }}>
                              <Typography variant="body2" sx={{ color: '#475569' }}>
                                Current Schedule: <strong style={{ color: '#1a1a1a' }}>{formatDateTime(codingTest.scheduled_at)}</strong>
                              </Typography>
                            </Box>
                            <Box sx={{ display: 'flex', gap: 1.5 }}>
                              <Button
                                variant="outlined"
                                size="small"
                                startIcon={<CalendarMonth sx={{ fontSize: 16 }} />}
                                onClick={async () => {
                                  resetFormState();
                                  setIsReschedule(true);
                                  setInterviewToSchedule('coding');
                                  setScheduleDialogOpen(true);
                                }}
                                sx={{ textTransform: 'none', borderRadius: '8px', fontWeight: 600, borderColor: '#1e3a8a', color: '#1e3a8a' }}
                              >
                                Reschedule
                              </Button>
                              <Button
                                variant="text"
                                size="small"
                                onClick={() => { fetchAttemptHistory(); setHistoryDialogOpen(true); }}
                                sx={{ textTransform: 'none', color: '#64748b', fontSize: '0.75rem' }}
                              >
                                View History
                              </Button>
                            </Box>
                          </Box>
                        ) : (
                          <Button
                            variant="contained"
                            startIcon={<CalendarMonth size={18} />}
                            disabled={
                              getCodingStatus() === "Completed" ||
                              getCodingStatus() === "Terminated"
                            }
                            onClick={async () => {
                              resetFormState();
                              setInterviewToSchedule('coding');
                              setScheduleDialogOpen(true);
                              try {
                                const response = await api.get(`/jobs/${application.jobId}`);
                                if (response.data.codingTestConfig) {
                                  setManualQuestion({
                                    question: response.data.codingTestConfig.question || '',
                                    timeLimit: response.data.codingTestConfig.timeLimit || 30,
                                    maxScore: response.data.codingTestConfig.maxScore || 100
                                  });
                                }
                              } catch (err) {
                                console.error('Failed to fetch job questions:', err);
                              }
                            }}
                            sx={{
                              backgroundColor: '#1e3a8a',
                              '&:hover': { backgroundColor: '#1e40af' },
                              textTransform: 'none',
                              borderRadius: '8px',
                              px: 3,
                              py: 1,
                              fontWeight: 600
                            }}
                          >
                            Schedule Interview
                          </Button>
                        )}
                      </Box>
                    </Box>
                  </Box>
                </CardContent>
              </Card>
            )}

            {selectedInterviews.includes("technical") && (
              <Card sx={{
                mb: 3,
                borderRadius: 3,
                boxShadow: "0 4px 20px rgba(0,0,0,0.05)",
                border: "1px solid #f0f0f0",
                position: 'relative',
                '&::before': {
                  content: '""',
                  position: 'absolute',
                  left: 0,
                  top: 0,
                  bottom: 0,
                  width: '4px',
                  backgroundColor: '#3b82f6',
                  borderRadius: '3px 0 0 3px'
                }
              }}>
                <CardContent sx={{ p: 3 }}>
                  <Box sx={{ display: 'flex', alignItems: 'flex-start', mb: 1 }}>
                    <Box sx={{
                      width: 48,
                      height: 48,
                      borderRadius: 2,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      backgroundColor: '#eff6ff',
                      mr: 2,
                      flexShrink: 0
                    }}>
                      <Badge sx={{ color: '#3b82f6' }} />
                    </Box>
                    <Box sx={{ flexGrow: 1 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', mb: 0.5 }}>
                        <Box sx={{
                          width: 24,
                          height: 24,
                          borderRadius: '50%',
                          backgroundColor: '#3b82f6',
                          color: 'white',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          fontSize: '0.75rem',
                          fontWeight: 'bold',
                          mr: 1.5
                        }}>
                          {INTERVIEW_CONFIG.roundNumber['technical']}
                        </Box>
                        <Typography variant="h6" sx={{ fontWeight: 700, color: '#1a1a1a', fontSize: '1.1rem' }}>
                          {INTERVIEW_CONFIG.labels['technical']}
                        </Typography>
                        {isTerminated && (
                          <Chip
                            label="Locked"
                            size="small"
                            sx={{ ml: 1.5, bgcolor: '#fff3e0', color: '#e65100', fontWeight: 600, fontSize: '0.7rem', border: '1px solid #ffe0b2' }}
                          />
                        )}
                      </Box>
                      <Typography variant="body2" sx={{ color: '#666', mb: 2, ml: 4.5 }}>
                        {INTERVIEW_CONFIG.descriptions['technical']}
                      </Typography>

                      <Box sx={{ ml: 4.5 }}>
                        {isTerminated && !techInterview?.completedAt && (
                          <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic', mb: 2 }}>
                            This stage is locked because the interview was terminated.
                          </Typography>
                        )}

                        {(() => {
                          const l2SelectSx = {
                            width: 240, borderRadius: "8px", fontSize: "0.875rem", fontWeight: 500,
                            backgroundColor: "#fff", border: '1px solid #e2e8f0',
                            "& .MuiSelect-select": { py: 1, px: 1.5 }, "& fieldset": { border: 'none' },
                            "&:hover": { borderColor: "#3b82f6" },
                          };

                          if (techInterview?.completedAt) {
                            return (
                              <Box sx={{
                                backgroundColor: '#f8fafc',
                                p: 2,
                                borderRadius: 2,
                                border: '1px solid #e2e8f0'
                              }}>
                                <Typography variant="body2" sx={{ color: '#475569', mb: 0.5 }}>
                                  Score: <strong style={{ color: '#1a1a1a' }}>{techInterview?.score} / {techInterview?.maxScore}</strong>
                                </Typography>
                                <Typography variant="body2" sx={{ color: '#475569' }}>
                                  Feedback: {techInterview?.feedback || 'No feedback provided.'}
                                </Typography>
                              </Box>
                            );
                          }

                          if (techInterview?.scheduledAt || techInterview?.scheduled_at || application?.status === 'technical_l2_interview_scheduled') {
                            const isL2 = techInterview?.technicalInterviewType !== 'AI_TECHNICAL' && techInterview?.technical_interview_type !== 'AI_TECHNICAL';
                            if (isL2) {
                              const techL2Completed = ['technical_completed', 'technical_l2_interview_completed'].includes(application?.status);
                              const techL2Cancelled = ['technical_cancelled', 'technical_l2_interview_cancelled'].includes(application?.status);
                              if (techL2Completed || techL2Cancelled) {
                                return (
                                  <Box sx={{ backgroundColor: '#f8fafc', p: 2, borderRadius: 2, border: '1px solid #e2e8f0' }}>
                                    <Box sx={{ mb: 1.5 }}>
                                      <Typography variant="body2" fontWeight={600}
                                        sx={{ color: techL2Cancelled ? '#d32f2f' : '#2e7d32' }}>
                                        {techL2Cancelled ? `${techL2Label} Cancelled` : `${techL2Label} Completed`}
                                      </Typography>
                                      {(techInterview?.completedAt || techInterview?.completed_at) && formatDateTime(techInterview.completedAt || techInterview.completed_at) && (
                                        <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block', mt: 0.25 }}>
                                          Completed On: <strong style={{ color: '#1a1a1a' }}>{formatDateTime(techInterview.completedAt || techInterview.completed_at)}</strong>
                                        </Typography>
                                      )}
                                    </Box>
                                    <FormControl size="small">
                                      <Select value="" displayEmpty disabled={isTerminated}
                                        onChange={(e) => handleInterviewAction('technical', e.target.value)}
                                        sx={l2SelectSx} IconComponent={ArrowDropDown}>
                                        <MenuItem value="" disabled>
                                          <Typography variant="body2" sx={{ color: "#64748b", fontWeight: 500 }}>Select Action</Typography>
                                        </MenuItem>
                                        <MenuItem value="schedule">Schedule Interview</MenuItem>
                                        <MenuItem value="cancel" disabled>{techL2Label} Cancelled</MenuItem>
                                        <MenuItem value="completed" disabled>{techL2Label} Completed</MenuItem>
                                      </Select>
                                    </FormControl>
                                  </Box>
                                );
                              }
                              const formattedScheduledAt = formatDateTime(techInterview.scheduledAt || techInterview.scheduled_at || application?.interview_scheduled_at);
                              return (
                                <Box sx={{ backgroundColor: '#f8fafc', p: 2, borderRadius: 2, border: '1px solid #e2e8f0' }}>
                                  {formattedScheduledAt && (
                                    <Typography variant="body2" sx={{ color: '#475569', mb: 1.5 }}>
                                      Scheduled on: <strong style={{ color: '#1a1a1a' }}>{formattedScheduledAt}</strong>
                                    </Typography>
                                  )}
                                  <FormControl size="small">
                                    <Select value="" displayEmpty disabled={isTerminated}
                                      onChange={(e) => handleInterviewAction('technical', e.target.value)}
                                      sx={l2SelectSx} IconComponent={ArrowDropDown}>
                                      <MenuItem value="" disabled>
                                        <Typography variant="body2" sx={{ color: "#64748b", fontWeight: 500 }}>Select Action</Typography>
                                      </MenuItem>
                                      <MenuItem value="schedule" disabled={!!(techInterview?.scheduledAt || techInterview?.scheduled_at || application?.status === 'technical_l2_interview_scheduled' || application?.status === 'technical_l2_interview_completed' || application?.status === 'technical_l2_interview_cancelled')}>Schedule Interview</MenuItem>
                                      <MenuItem value="cancel" disabled={!(techInterview?.scheduledAt || techInterview?.scheduled_at || application?.status === 'technical_l2_interview_scheduled')}>{techL2Label} Cancelled</MenuItem>
                                      <MenuItem value="completed" disabled={!(techInterview?.scheduledAt || techInterview?.scheduled_at || application?.status === 'technical_l2_interview_scheduled')}>{techL2Label} Completed</MenuItem>
                                    </Select>
                                  </FormControl>
                                </Box>
                              );
                            }
                          }

                          const techL2Completed = ['technical_completed', 'technical_l2_interview_completed'].includes(application?.status);
                          const techL2Cancelled = ['technical_cancelled', 'technical_l2_interview_cancelled'].includes(application?.status);

                          if (techL2Completed || techL2Cancelled) {
                             const isL2 = techInterview?.technicalInterviewType !== 'AI_TECHNICAL' && techInterview?.technical_interview_type !== 'AI_TECHNICAL';
                             if (isL2) {
                                return (
                                  <Box sx={{ backgroundColor: '#f8fafc', p: 2, borderRadius: 2, border: '1px solid #e2e8f0' }}>
                                    <Box sx={{ mb: 1.5 }}>
                                      <Typography variant="body2" fontWeight={600}
                                        sx={{ color: techL2Cancelled ? '#d32f2f' : '#2e7d32' }}>
                                        {techL2Cancelled ? `${techL2Label} Cancelled` : `${techL2Label} Completed`}
                                      </Typography>
                                      {(techInterview?.completedAt || techInterview?.completed_at) && formatDateTime(techInterview.completedAt || techInterview.completed_at) && (
                                        <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block', mt: 0.25 }}>
                                          Completed On: <strong style={{ color: '#1a1a1a' }}>{formatDateTime(techInterview.completedAt || techInterview.completed_at)}</strong>
                                        </Typography>
                                      )}
                                    </Box>
                                    <FormControl size="small">
                                      <Select value="" displayEmpty disabled={isTerminated}
                                        onChange={(e) => handleInterviewAction('technical', e.target.value)}
                                        sx={l2SelectSx} IconComponent={ArrowDropDown}>
                                        <MenuItem value="" disabled>
                                          <Typography variant="body2" sx={{ color: "#64748b", fontWeight: 500 }}>Select Action</Typography>
                                        </MenuItem>
                                        <MenuItem value="schedule">Schedule Interview</MenuItem>
                                        <MenuItem value="cancel" disabled>{techL2Label} Cancelled</MenuItem>
                                        <MenuItem value="completed" disabled>{techL2Label} Completed</MenuItem>
                                      </Select>
                                    </FormControl>
                                  </Box>
                                );
                             }
                          }

                          if (['ai_technical_interview_scheduled', 'ai_technical_interview_completed'].includes(application?.status)) {
                            const aiTechScheduledAt = techInterview?.scheduled_at || techInterview?.scheduledAt || application?.interview_scheduled_at;
                            return (
                              <Box>
                                <Box sx={{ backgroundColor: '#f8fafc', p: 2, borderRadius: 2, border: '1px solid #e2e8f0', mb: 1.5 }}>
                                  <Typography variant="body2" fontWeight={600} sx={{ color: application?.status === 'ai_technical_interview_completed' ? '#2e7d32' : '#1e3a8a', mb: 0.5 }}>
                                    {application?.status === 'ai_technical_interview_completed'
                                      ? `${INTERVIEW_CONFIG.labels.technical} Completed`
                                      : `${INTERVIEW_CONFIG.labels.technical} Scheduled`}
                                  </Typography>
                                  {aiTechScheduledAt && (
                                    <Typography variant="body2" sx={{ color: '#475569' }}>
                                      Current Schedule: <strong style={{ color: '#1a1a1a' }}>{formatDateTime(aiTechScheduledAt)}</strong>
                                    </Typography>
                                  )}
                                </Box>
                                {!isTerminated && (
                                  <Box sx={{ display: 'flex', gap: 1.5, mt: 1, alignItems: 'center' }}>
                                    <FormControl size="small">
                                      <Select value="" displayEmpty
                                        onChange={(e) => handleInterviewAction('technical', e.target.value)}
                                        sx={l2SelectSx} IconComponent={ArrowDropDown}>
                                        <MenuItem value="" disabled>
                                          <Typography variant="body2" sx={{ color: "#64748b", fontWeight: 500 }}>Select Action</Typography>
                                        </MenuItem>
                                        <MenuItem value="schedule">Schedule Interview</MenuItem>
                                      </Select>
                                    </FormControl>
                                    <Button
                                      variant="text"
                                      size="small"
                                      onClick={() => { fetchAttemptHistory(); setHistoryDialogOpen(true); }}
                                      sx={{ textTransform: 'none', color: '#64748b', fontSize: '0.75rem' }}
                                    >
                                      View History
                                    </Button>
                                  </Box>
                                )}
                              </Box>
                            );
                          }

                          if (!isTerminated) {
                            return (
                              <FormControl size="small">
                                <Select
                                  value=""
                                  displayEmpty
                                  onChange={(e) => handleInterviewAction('technical', e.target.value)}
                                  sx={{
                                    width: 240,
                                    borderRadius: "8px",
                                    fontSize: "0.875rem",
                                    fontWeight: 500,
                                    backgroundColor: "#fff",
                                    border: '1px solid #e2e8f0',
                                    "& .MuiSelect-select": { py: 1, px: 1.5 },
                                    "& fieldset": { border: 'none' },
                                    "&:hover": { borderColor: "#3b82f6" },
                                  }}
                                  IconComponent={ArrowDropDown}
                                >
                                  <MenuItem value="" disabled>
                                    <Typography variant="body2" sx={{ color: "#64748b", fontWeight: 500 }}>Select Action</Typography>
                                  </MenuItem>
                                  <MenuItem value="schedule" disabled={!!(techInterview?.scheduledAt || techInterview?.scheduled_at || application?.status === 'technical_l2_interview_scheduled' || application?.status === 'technical_l2_interview_completed' || application?.status === 'technical_l2_interview_cancelled')}>Schedule Interview</MenuItem>
                                  <MenuItem value="cancel" disabled={!(techInterview?.scheduledAt || techInterview?.scheduled_at || application?.status === 'technical_l2_interview_scheduled')}>{techL2Label} Cancelled</MenuItem>
                                  <MenuItem value="completed" disabled={!(techInterview?.scheduledAt || techInterview?.scheduled_at || application?.status === 'technical_l2_interview_scheduled')}>{techL2Label} Completed</MenuItem>
                                </Select>
                              </FormControl>
                            );
                          }

                          return null;
                        })()}
                      </Box>
                    </Box>
                  </Box>
                </CardContent>
              </Card>
            )}

            {selectedInterviews.includes("hr") && (
              <Card sx={{
                mb: 3,
                borderRadius: 3,
                boxShadow: "0 4px 20px rgba(0,0,0,0.05)",
                border: "1px solid #f0f0f0",
                position: 'relative',
                '&::before': {
                  content: '""',
                  position: 'absolute',
                  left: 0,
                  top: 0,
                  bottom: 0,
                  width: '4px',
                  backgroundColor: '#0d9488',
                  borderRadius: '3px 0 0 3px'
                }
              }}>
                <CardContent sx={{ p: 3 }}>
                  <Box sx={{ display: 'flex', alignItems: 'flex-start', mb: 1 }}>
                    <Box sx={{
                      width: 48,
                      height: 48,
                      borderRadius: 2,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      backgroundColor: '#f0fdfa',
                      mr: 2,
                      flexShrink: 0
                    }}>
                      <Group sx={{ color: '#0d9488' }} />
                    </Box>
                    <Box sx={{ flexGrow: 1 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', mb: 0.5 }}>
                        <Box sx={{
                          width: 24,
                          height: 24,
                          borderRadius: '50%',
                          backgroundColor: '#0d9488',
                          color: 'white',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          fontSize: '0.75rem',
                          fontWeight: 'bold',
                          mr: 1.5
                        }}>
                          {INTERVIEW_CONFIG.roundNumber['hr']}
                        </Box>
                        <Typography variant="h6" sx={{ fontWeight: 700, color: '#1a1a1a', fontSize: '1.1rem' }}>
                          {INTERVIEW_CONFIG.labels['hr']}
                        </Typography>
                        {isTerminated && (
                          <Chip
                            label="Locked"
                            size="small"
                            sx={{ ml: 1.5, bgcolor: '#fff3e0', color: '#e65100', fontWeight: 600, fontSize: '0.7rem', border: '1px solid #ffe0b2' }}
                          />
                        )}
                      </Box>
                      <Typography variant="body2" sx={{ color: '#666', mb: 2, ml: 4.5 }}>
                        {INTERVIEW_CONFIG.descriptions['hr']}
                      </Typography>

                      <Box sx={{ ml: 4.5 }}>
                        {isTerminated && !hrInterview?.completedAt && (
                          <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic', mb: 2 }}>
                            This stage is locked because the interview was terminated.
                          </Typography>
                        )}

                        {(() => {
                          const hrL2SelectSx = {
                            width: 220, borderRadius: "8px", fontSize: "0.875rem", fontWeight: 500,
                            backgroundColor: "#fff", border: '1px solid #e2e8f0',
                            "& .MuiSelect-select": { py: 1, px: 1.5 }, "& fieldset": { border: 'none' },
                            "&:hover": { borderColor: "#0d9488" },
                          };

                          if (hrInterview?.completedAt) {
                            return (
                              <Box sx={{
                                backgroundColor: '#f8fafc',
                                p: 2,
                                borderRadius: 2,
                                border: '1px solid #e2e8f0'
                              }}>
                                <Typography variant="body2" sx={{ color: '#475569', mb: 0.5 }}>
                                  Score: <strong style={{ color: '#1a1a1a' }}>{hrInterview?.score} / {hrInterview?.maxScore}</strong>
                                </Typography>
                                <Typography variant="body2" sx={{ color: '#475569' }}>
                                  Feedback: {hrInterview?.feedback || 'No feedback provided.'}
                                </Typography>
                              </Box>
                            );
                          }

                          if (hrInterview?.scheduledAt || hrInterview?.scheduled_at || application?.status === 'hr_l2_interview_scheduled') {
                            const isAIHR =
                              !!(hrInterview?.interview_token || hrInterview?.interviewToken) ||
                              ['ai_hr_interview_scheduled', 'ai_hr_interview_completed'].includes(application?.status);

                            if (!isAIHR) {
                              const hrL2Completed = ['hr_completed', 'hr_l2_interview_completed'].includes(application?.status);
                              const hrL2Cancelled = ['hr_cancelled', 'hr_l2_interview_cancelled'].includes(application?.status);
                              if (hrL2Completed || hrL2Cancelled) {
                                return (
                                  <Box sx={{ backgroundColor: '#f8fafc', p: 2, borderRadius: 2, border: '1px solid #e2e8f0' }}>
                                    <Box sx={{ mb: 1.5 }}>
                                      <Typography variant="body2" fontWeight={600}
                                        sx={{ color: hrL2Cancelled ? '#d32f2f' : '#2e7d32' }}>
                                        {hrL2Cancelled ? `${hrL2Label} Cancelled` : `${hrL2Label} Completed`}
                                      </Typography>
                                      {(hrInterview?.completedAt || hrInterview?.completed_at) && (
                                        <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block', mt: 0.25 }}>
                                          Completed On: <strong style={{ color: '#1a1a1a' }}>{formatDateTime(hrInterview.completedAt || hrInterview.completed_at)}</strong>
                                        </Typography>
                                      )}
                                    </Box>
                                    <FormControl size="small">
                                      <Select value="" displayEmpty disabled={isTerminated}
                                        onChange={(e) => handleInterviewAction('hr', e.target.value)}
                                        sx={hrL2SelectSx} IconComponent={ArrowDropDown}>
                                        <MenuItem value="" disabled>
                                          <Typography variant="body2" sx={{ color: "#64748b", fontWeight: 500 }}>Select Action</Typography>
                                        </MenuItem>
                                        <MenuItem value="schedule">Schedule Interview</MenuItem>
                                        <MenuItem value="cancel" disabled>{hrL2Label} Cancelled</MenuItem>
                                        <MenuItem value="completed" disabled>{hrL2Label} Completed</MenuItem>
                                      </Select>
                                    </FormControl>
                                  </Box>
                                );
                              }
                              const formattedScheduledAt = formatDateTime(hrInterview.scheduledAt || hrInterview.scheduled_at || application?.interview_scheduled_at);
                              return (
                                <Box sx={{ backgroundColor: '#f8fafc', p: 2, borderRadius: 2, border: '1px solid #e2e8f0' }}>
                                  {formattedScheduledAt && (
                                    <Typography variant="body2" sx={{ color: '#475569', mb: 1.5 }}>
                                      Scheduled on: <strong style={{ color: '#1a1a1a' }}>{formattedScheduledAt}</strong>
                                    </Typography>
                                  )}
                                  <FormControl size="small">
                                    <Select value="" displayEmpty disabled={isTerminated}
                                      onChange={(e) => handleInterviewAction('hr', e.target.value)}
                                      sx={hrL2SelectSx} IconComponent={ArrowDropDown}>
                                      <MenuItem value="" disabled>
                                        <Typography variant="body2" sx={{ color: "#64748b", fontWeight: 500 }}>Select Action</Typography>
                                      </MenuItem>
                                      <MenuItem value="schedule" disabled={!!(hrInterview?.scheduledAt || hrInterview?.scheduled_at || application?.status === 'hr_l2_interview_scheduled')}>Schedule Interview</MenuItem>
                                      <MenuItem value="cancel" disabled={!(hrInterview?.scheduledAt || hrInterview?.scheduled_at || application?.status === 'hr_l2_interview_scheduled')}>{hrL2Label} Cancelled</MenuItem>
                                      <MenuItem value="completed" disabled={!(hrInterview?.scheduledAt || hrInterview?.scheduled_at || application?.status === 'hr_l2_interview_scheduled')}>{hrL2Label} Completed</MenuItem>
                                    </Select>
                                  </FormControl>
                                </Box>
                              );
                            }
                          }

                          const hrL2Completed = ['hr_completed', 'hr_l2_interview_completed'].includes(application?.status);
                          const hrL2Cancelled = ['hr_cancelled', 'hr_l2_interview_cancelled'].includes(application?.status);

                          if (hrL2Completed || hrL2Cancelled) {
                             const isAIHR = ['ai_hr_interview_scheduled', 'ai_hr_interview_completed'].includes(application?.status);
                             if (!isAIHR) {
                                return (
                                  <Box sx={{ backgroundColor: '#f8fafc', p: 2, borderRadius: 2, border: '1px solid #e2e8f0' }}>
                                    <Box sx={{ mb: 1.5 }}>
                                      <Typography variant="body2" fontWeight={600}
                                        sx={{ color: hrL2Cancelled ? '#d32f2f' : '#2e7d32' }}>
                                        {hrL2Cancelled ? `${hrL2Label} Cancelled` : `${hrL2Label} Completed`}
                                      </Typography>
                                      {(hrInterview?.completedAt || hrInterview?.completed_at) && (
                                        <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block', mt: 0.25 }}>
                                          Completed On: <strong style={{ color: '#1a1a1a' }}>{formatDateTime(hrInterview.completedAt || hrInterview.completed_at)}</strong>
                                        </Typography>
                                      )}
                                    </Box>
                                    <FormControl size="small">
                                      <Select value="" displayEmpty disabled={isTerminated}
                                        onChange={(e) => handleInterviewAction('hr', e.target.value)}
                                        sx={hrL2SelectSx} IconComponent={ArrowDropDown}>
                                        <MenuItem value="" disabled>
                                          <Typography variant="body2" sx={{ color: "#64748b", fontWeight: 500 }}>Select Action</Typography>
                                        </MenuItem>
                                        <MenuItem value="schedule">Schedule Interview</MenuItem>
                                        <MenuItem value="cancel" disabled>{hrL2Label} Cancelled</MenuItem>
                                        <MenuItem value="completed" disabled>{hrL2Label} Completed</MenuItem>
                                      </Select>
                                    </FormControl>
                                  </Box>
                                );
                             }
                          }

                          if (['ai_hr_interview_scheduled', 'ai_hr_interview_completed'].includes(application?.status)) {
                            const aiHRScheduledAt = hrInterview?.scheduled_at || hrInterview?.scheduledAt || application?.interview_scheduled_at;
                            return (
                              <Box>
                                <Box sx={{ backgroundColor: '#f8fafc', p: 2, borderRadius: 2, border: '1px solid #e2e8f0', mb: 1.5 }}>
                                  <Typography variant="body2" fontWeight={600} sx={{ color: application?.status === 'ai_hr_interview_completed' ? '#2e7d32' : '#0d9488', mb: 0.5 }}>
                                    {application?.status === 'ai_hr_interview_completed'
                                      ? `${INTERVIEW_CONFIG.labels.hr} Completed`
                                      : `${INTERVIEW_CONFIG.labels.hr} Scheduled`}
                                  </Typography>
                                  {aiHRScheduledAt && (
                                    <Typography variant="body2" sx={{ color: '#475569' }}>
                                      Current Schedule: <strong style={{ color: '#1a1a1a' }}>{formatDateTime(aiHRScheduledAt)}</strong>
                                    </Typography>
                                  )}
                                </Box>
                                {!isTerminated && (
                                  <Box sx={{ display: 'flex', gap: 1.5, mt: 1, alignItems: 'center' }}>
                                    <FormControl size="small">
                                      <Select value="" displayEmpty
                                        onChange={(e) => handleInterviewAction('hr', e.target.value)}
                                        sx={hrL2SelectSx} IconComponent={ArrowDropDown}>
                                        <MenuItem value="" disabled>
                                          <Typography variant="body2" sx={{ color: "#64748b", fontWeight: 500 }}>Select Action</Typography>
                                        </MenuItem>
                                        <MenuItem value="schedule">Schedule Interview</MenuItem>
                                      </Select>
                                    </FormControl>
                                    <Button
                                      variant="text"
                                      size="small"
                                      onClick={() => { fetchAttemptHistory(); setHistoryDialogOpen(true); }}
                                      sx={{ textTransform: 'none', color: '#64748b', fontSize: '0.75rem' }}
                                    >
                                      View History
                                    </Button>
                                  </Box>
                                )}
                              </Box>
                            );
                          }

                          if (!isTerminated) {
                            return (
                              <FormControl size="small">
                                <Select
                                  value=""
                                  displayEmpty
                                  onChange={(e) => handleInterviewAction('hr', e.target.value)}
                                  sx={{
                                    width: 220,
                                    borderRadius: "8px",
                                    fontSize: "0.875rem",
                                    fontWeight: 500,
                                    backgroundColor: "#fff",
                                    border: '1px solid #0d9488',
                                    "& .MuiSelect-select": { py: 1, px: 1.5 },
                                    "& fieldset": { border: 'none' },
                                    "&:hover": { borderColor: "#0d9488" },
                                  }}
                                  IconComponent={ArrowDropDown}
                                >
                                  <MenuItem value="" disabled>
                                    <Typography variant="body2" sx={{ color: "#64748b", fontWeight: 500 }}>Select Action</Typography>
                                  </MenuItem>
                                  <MenuItem value="schedule" disabled={!!(hrInterview?.scheduledAt || hrInterview?.scheduled_at || application?.status === 'hr_l2_interview_scheduled' || application?.status === 'hr_l2_interview_completed' || application?.status === 'hr_l2_interview_cancelled')}>Schedule Interview</MenuItem>
                                  <MenuItem value="cancel" disabled={!(hrInterview?.scheduledAt || hrInterview?.scheduled_at || application?.status === 'hr_l2_interview_scheduled')}>{hrL2Label} Cancelled</MenuItem>
                                  <MenuItem value="completed" disabled={!(hrInterview?.scheduledAt || hrInterview?.scheduled_at || application?.status === 'hr_l2_interview_scheduled')}>{hrL2Label} Completed</MenuItem>
                                </Select>
                              </FormControl>
                            );
                          }

                          return null;
                        })()}
                      </Box>
                    </Box>
                  </Box>
                </CardContent>
              </Card>
            )}

            {/* ✅ FINAL ACTION BUTTONS BELOW ALL INTERVIEWS */}
            <Box sx={{ display: "flex", gap: 2, mt: 4, mb: 2 }}>
              <Button
                variant="contained"
                disabled={isSelected || isRejected || isLocked}
                onClick={() => handleOpenDecisionDialog("selected")}
                startIcon={<PersonAdd />}
                sx={{
                  backgroundColor: '#1e3a8a',
                  '&:hover': { backgroundColor: '#1e40af' },
                  textTransform: 'none',
                  borderRadius: '8px',
                  px: 3,
                  py: 1.2,
                  fontWeight: 600,
                  boxShadow: 'none'
                }}
              >
                Candidate Selected
              </Button>

              <Button
                variant="outlined"
                color="error"
                disabled={isLocked}
                onClick={() => handleOpenDecisionDialog("rejected")}
                startIcon={<HighlightOff />}
                sx={{
                  textTransform: 'none',
                  borderRadius: '8px',
                  px: 3,
                  py: 1.2,
                  fontWeight: 600,
                  borderColor: '#ef4444',
                  color: '#ef4444',
                  '&:hover': {
                    borderColor: '#dc2626',
                    backgroundColor: '#fef2f2'
                  }
                }}
              >
                Reject Candidate
              </Button>
            </Box>

            {/* ── Post Selection Document Collection ── */}
            <Box sx={{
              mt: 4, p: 3,
              border: `2px solid ${isSelected ? '#1E3A8A' : '#e0e0e0'}`,
              borderRadius: 2,
              bgcolor: isSelected ? '#F8FAFC' : '#fafafa',
              opacity: isSelected ? 1 : 0.75,
              transition: 'all 0.3s ease',
            }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 0.5 }}>
                <Typography variant="h6" fontWeight="bold" sx={{ color: isSelected ? '#1E3A8A' : '#9e9e9e' }}>
                  Post Selection Documents
                </Typography>
                {!isSelected && (
                  <Chip
                    label="Locked"
                    size="small"
                    sx={{ bgcolor: '#f5f5f5', color: '#9e9e9e', fontWeight: 600, fontSize: '0.7rem', border: '1px solid #e0e0e0' }}
                  />
                )}
                {isSelected && (
                  <Chip
                    label="Enabled"
                    size="small"
                    sx={{ bgcolor: '#e8f5e9', color: '#2e7d32', fontWeight: 600, fontSize: '0.7rem', border: '1px solid #c8e6c9' }}
                  />
                )}
              </Box>

              <Typography variant="body2" color="text.secondary" sx={{ mb: isSelected ? 3 : 1.5 }}>
                Request the selected candidate to submit required documents before onboarding.
              </Typography>

              {!isSelected && (
                <Alert
                  severity="info"
                  sx={{ mb: 3, borderRadius: 1.5, bgcolor: '#f3f4f6', border: '1px solid #e0e0e0', color: '#555' }}
                  icon={<Schedule sx={{ color: '#9e9e9e' }} />}
                >
                  Select the candidate first to enable document collection and document request email sending.
                </Alert>
              )}

              <Typography variant="subtitle2" fontWeight="bold" sx={{ mb: 1.5, color: isSelected ? '#1a1a1a' : '#bdbdbd' }}>
                Mandatory Documents Checklist
              </Typography>
              <Box sx={{ bgcolor: 'white', borderRadius: 1.5, border: '1px solid #e0e0e0', p: 2, mb: 3 }}>
                {[
                  { key: 'govId',            label: 'Government ID Proof',         sub: 'Aadhaar / PAN / Passport / Driving License' },
                  { key: 'photo',            label: 'Passport Size Photo' },
                  { key: 'resume',           label: 'Resume / CV' },
                  { key: 'educationCerts',   label: 'Educational Certificates',    sub: '10th / 12th / Degree / Post Graduation' },
                  { key: 'experienceLetters',label: 'Experience Letters',          sub: 'if applicable' },
                  { key: 'relievingLetter',  label: 'Relieving Letter',            sub: 'if experienced' },
                  { key: 'salarySlips',      label: 'Salary Slips',               sub: 'Last 3 Months if experienced' },
                  { key: 'addressProof',     label: 'Address Proof' },
                  { key: 'bankDetails',      label: 'Bank Account Details',        sub: 'Cancelled Cheque / Bank Passbook' },
                  { key: 'offerAcceptance',  label: 'Offer Acceptance Copy' },
                  { key: 'bgvConsent',       label: 'Background Verification Consent' },
                ].map(({ key, label, sub }) => (
                  <FormControlLabel
                    key={key}
                    disabled={!isSelected}
                    control={
                      <Checkbox
                        checked={docChecklist[key]}
                        onChange={(e) => setDocChecklist(prev => ({ ...prev, [key]: e.target.checked }))}
                        size="small"
                        disabled={!isSelected}
                        sx={{ color: '#1E3A8A', '&.Mui-checked': { color: '#1E3A8A' } }}
                      />
                    }
                    label={
                      <Box>
                        <Typography variant="body2" fontWeight={500} sx={{ color: isSelected ? 'inherit' : '#bdbdbd' }}>{label}</Typography>
                        {sub && <Typography variant="caption" sx={{ color: isSelected ? 'text.secondary' : '#bdbdbd' }}>{sub}</Typography>}
                      </Box>
                    }
                    sx={{ display: 'flex', alignItems: 'flex-start', mb: 0.5 }}
                  />
                ))}
              </Box>

              <Typography variant="subtitle2" fontWeight="bold" sx={{ mb: 1, color: isSelected ? '#1a1a1a' : '#bdbdbd' }}>
                Additional Documents Required
              </Typography>
              <TextField
                fullWidth multiline rows={3}
                disabled={!isSelected}
                value={docAdditional}
                onChange={(e) => setDocAdditional(e.target.value)}
                placeholder={"Enter additional document names (one per line)\ne.g. NOC Letter\nPortfolio\nGitHub Profile"}
                variant="outlined" size="small"
                sx={{ mb: 2, bgcolor: 'white' }}
              />

              <TextField
                fullWidth label="Recruiter Email"
                disabled={!isSelected}
                value={docEmail}
                onChange={(e) => setDocEmail(e.target.value)}
                placeholder="Candidate should send documents to (e.g. hr@company.com)"
                variant="outlined" size="small" type="email"
                sx={{ mb: 2, bgcolor: 'white' }}
              />

              <TextField
                fullWidth label="Submission Deadline"
                disabled={!isSelected}
                value={docDeadline}
                onChange={(e) => setDocDeadline(e.target.value)}
                type="date" variant="outlined" size="small"
                InputLabelProps={{ shrink: true }}
                sx={{ mb: 2, bgcolor: 'white' }}
              />

              <TextField
                fullWidth label="Recruiter Instructions" multiline rows={2}
                disabled={!isSelected}
                value={docInstructions}
                onChange={(e) => setDocInstructions(e.target.value)}
                placeholder={"Additional instructions\ne.g. Please submit PDF documents only. Maximum size 5MB."}
                variant="outlined" size="small"
                sx={{ mb: 3, bgcolor: 'white' }}
              />

              <Button
                variant="contained"
                disabled={!isSelected || docSendingEmail || docEmailSent}
                startIcon={docSendingEmail ? <CircularProgress size={16} sx={{ color: 'white' }} /> : null}
                onClick={async () => {
                  setDocSendingEmail(true);
                  try {
                    const response = await api.post(`/applications/${id}/document-request`, {
                      checklist: docChecklist,
                      additionalDocs: docAdditional,
                      recruiterEmail: docEmail,
                      instructions: docInstructions,
                      deadline: docDeadline
                        ? new Date(docDeadline).toLocaleDateString('en-IN', { day: '2-digit', month: '2-digit', year: 'numeric' })
                        : '',
                    });
                    setDocEmailSent(true);
                    setDocTotalSent(response.data.totalDocs || 0);
                    setSuccess('Document request email sent successfully!');
                  } catch (err) {
                    setError(err.response?.data?.message || 'Failed to send document request email');
                  } finally {
                    setDocSendingEmail(false);
                  }
                }}
                sx={{ bgcolor: '#1E3A8A', '&:hover': { bgcolor: '#1E40AF' }, textTransform: 'none', fontWeight: 'bold' }}
              >
                {docSendingEmail ? 'Sending...' : docEmailSent ? 'Email Sent' : 'Send Document Request Email'}
              </Button>

              {docEmailSent && (
                <Box sx={{ mt: 3, p: 2, bgcolor: '#e8f5e9', borderRadius: 1.5, border: '1px solid #c8e6c9' }}>
                  <Typography variant="body2" fontWeight="bold" sx={{ color: '#2e7d32', mb: 1 }}>
                    ✓ Document Request Sent
                  </Typography>
                  <Typography variant="body2" color="text.secondary">Pending Documents: 0 / {docTotalSent}</Typography>
                  <Typography variant="body2" color="text.secondary">Submitted Documents: 0 / {docTotalSent}</Typography>
                  <Typography variant="body2" color="text.secondary">Verified Documents: 0 / {docTotalSent}</Typography>
                </Box>
              )}
            </Box>

            </>
           )}
          </Grid>
        </Grid>

        <Dialog open={scheduleDialogOpen} onClose={() => setScheduleDialogOpen(false)} maxWidth="sm" fullWidth>
          <DialogTitle>
            {interviewToSchedule === 'coding'
              ? (isReschedule ? 'Reschedule AI Coding Interview' : 'Schedule AI Coding Interview')
              : interviewToSchedule === 'technical' && technicalInterviewType === 'AI_TECHNICAL'
                ? (isReschedule
                    ? (isBPO ? `Reschedule ${INTERVIEW_CONFIG.labels.technical}` : 'Reschedule AI Technical Interview')
                    : (isBPO ? `Schedule ${INTERVIEW_CONFIG.labels.technical}` : 'Schedule AI Technical Interview'))
                : interviewToSchedule === 'technical'
                  ? (isBPO ? `Schedule ${INTERVIEW_CONFIG.labels.technical}` : 'Schedule Technical Interview')
                  : interviewToSchedule === 'hr' && hrInterviewType === 'AI_HR_INTERVIEW'
                    ? (isReschedule
                        ? (isBPO ? `Reschedule ${INTERVIEW_CONFIG.labels.hr}` : 'Reschedule AI HR & Behavioral Evaluation')
                        : (isBPO ? `Schedule ${INTERVIEW_CONFIG.labels.hr}` : 'Schedule AI HR & Behavioral Evaluation'))
                    : interviewToSchedule === 'hr' && hrInterviewType === 'HR_L2_INTERVIEW'
                      ? (isBPO ? `Schedule ${INTERVIEW_CONFIG.labels.hr} L2 Interview` : 'Schedule HR L2 Interview')
                      : isReschedule
                        ? (isBPO ? `Reschedule ${INTERVIEW_CONFIG.labels.hr}` : 'Reschedule HR Interview')
                        : (isBPO ? `Schedule ${INTERVIEW_CONFIG.labels.hr}` : 'Schedule HR Interview')}
          </DialogTitle>
          <DialogContent>
            <Box sx={{ mt: 2, display: 'flex', flexDirection: 'column', gap: 2 }}>
              <Typography variant="body2">
                Candidate: <strong>{application.candidate?.firstName} {application.candidate?.lastName}</strong>
              </Typography>
              <TextField
                  label="Interview Date and Time"
                  type="datetime-local"
                  fullWidth
                  value={scheduledDate}
                  onChange={(e) => setScheduledDate(e.target.value)}
                  InputLabelProps={{
                    shrink: true,
                  }}
                  required
              />
              {interviewToSchedule === 'technical' && (() => {
                const aiTechScheduled =
                  application?.ai_tech_scheduled ||
                  (techInterview?.technicalInterviewType === 'AI_TECHNICAL' && !!techInterview?.scheduledAt) ||
                  ['ai_technical_interview_scheduled', 'ai_technical_interview_completed'].includes(application?.status);
                const aiTechScheduledDate =
                  (techInterview?.technicalInterviewType === 'AI_TECHNICAL' ? techInterview?.scheduledAt : null) ||
                  (aiTechScheduled ? (application?.interviewScheduledAt || application?.interview_scheduled_at) : null);
                return (
                  <FormControl component="fieldset" required>
                    <FormLabel component="legend">Interview Type *</FormLabel>
                    <RadioGroup
                      value={technicalInterviewType}
                      onChange={(e) => setTechnicalInterviewType(e.target.value)}
                    >
                      <FormControlLabel
                        value="AI_TECHNICAL"
                        control={<Radio />}
                        label={
                          aiTechScheduled ? (
                            <Box sx={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 0.75 }}>
                              <span>{isBPO ? INTERVIEW_CONFIG.labels.technical : 'AI Technical Interview'}</span>
                              {isReschedule && technicalInterviewType === 'AI_TECHNICAL'
                                ? <strong style={{ color: '#1e3a8a', fontSize: '0.85rem' }}>(Rescheduling)</strong>
                                : (
                                  <Button
                                    size="small"
                                    onClick={(e) => { e.stopPropagation(); setTechnicalInterviewType('AI_TECHNICAL'); setIsReschedule(true); }}
                                    sx={{ textTransform: 'none', color: '#1e3a8a', fontWeight: 600, fontSize: '0.75rem', p: '0 4px', minWidth: 'unset', lineHeight: 1.5, textDecoration: 'underline', '&:hover': { background: 'transparent', textDecoration: 'underline' } }}
                                  >
                                    Reschedule
                                  </Button>
                                )
                              }
                              {aiTechScheduledDate && !isReschedule && (
                                <Typography variant="caption" sx={{ color: '#64748b' }}>
                                  (Scheduled: {formatDateTime(aiTechScheduledDate)})
                                </Typography>
                              )}
                            </Box>
                          ) : (
                            isBPO ? INTERVIEW_CONFIG.labels.technical : 'AI Technical Interview'
                          )
                        }
                      />
                      <FormControlLabel value="TECHNICAL_L2" control={<Radio />} label={isBPO ? 'Customer Support L2 Interview' : 'Technical L2 Interview'} />
                    </RadioGroup>
                  </FormControl>
                );
              })()}

              {interviewToSchedule === 'hr' && (() => {
                const aiHRScheduled =
                  application?.ai_hr_scheduled ||
                  ['ai_hr_interview_scheduled', 'ai_hr_interview_completed'].includes(application?.status) ||
                  (!!(hrInterview?.interview_token || hrInterview?.interviewToken) && !!hrInterview?.scheduledAt);
                const aiHRScheduledDate =
                  hrInterview?.scheduledAt ||
                  (aiHRScheduled ? (application?.interviewScheduledAt || application?.interview_scheduled_at) : null);
                return (
                  <FormControl component="fieldset" required>
                    <FormLabel component="legend">HR Interview Type *</FormLabel>
                    <RadioGroup
                      value={hrInterviewType}
                      onChange={(e) => setHrInterviewType(e.target.value)}
                    >
                      <FormControlLabel
                        value="AI_HR_INTERVIEW"
                        control={<Radio />}
                        label={
                          <Box>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                              <span>{isBPO ? INTERVIEW_CONFIG.labels.hr : 'AI HR & Behavioral Evaluation'}</span>
                              {aiHRScheduled && (
                                isReschedule && hrInterviewType === 'AI_HR_INTERVIEW'
                                  ? <Chip size="small" label="Rescheduling" sx={{ bgcolor: '#e8f5e9', color: '#2e7d32', fontWeight: 600, height: 20, fontSize: '0.7rem' }} />
                                  : (
                                    <Button
                                      size="small"
                                      onClick={(e) => { e.stopPropagation(); setHrInterviewType('AI_HR_INTERVIEW'); setIsReschedule(true); }}
                                      sx={{ textTransform: 'none', color: '#0d9488', fontWeight: 600, fontSize: '0.75rem', p: '0 4px', minWidth: 'unset', lineHeight: 1.5, textDecoration: 'underline', '&:hover': { background: 'transparent', textDecoration: 'underline' } }}
                                    >
                                      Reschedule
                                    </Button>
                                  )
                              )}
                            </Box>
                            {aiHRScheduled && aiHRScheduledDate && !isReschedule && (
                              <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block', mt: 0.25 }}>
                                Scheduled On: <strong style={{ color: '#1a1a1a' }}>{formatDateTime(aiHRScheduledDate)}</strong>
                              </Typography>
                            )}
                          </Box>
                        }
                      />
                      <FormControlLabel value="HR_L2_INTERVIEW" control={<Radio />} label={isBPO ? `${INTERVIEW_CONFIG.labels.hr} L2 Interview` : 'HR L2 Interview'} />
                    </RadioGroup>
                  </FormControl>
                );
              })()}

              {(interviewToSchedule === 'coding' ||
                (interviewToSchedule === 'technical' && technicalInterviewType === 'AI_TECHNICAL') ||
                (interviewToSchedule === 'hr' && hrInterviewType === 'AI_HR_INTERVIEW')) && (
                <>
                  {interviewToSchedule === 'coding' && (
                    <FormControl fullWidth>
                      <InputLabel>Coding Process Type</InputLabel>
                      <Select
                        value={codingTestType}
                        label="Coding Process Type"
                        onChange={(e) => setCodingTestType(e.target.value)}
                      >
                        <MenuItem value="AI-Generated">AI-Generated Interview</MenuItem>
                        <MenuItem value="AI-Manual">AI with Manual questions generated Interview</MenuItem>
                      </Select>
                    </FormControl>
                  )}

                  <FormControl fullWidth>
                    <InputLabel>Experience Level</InputLabel>
                    <Select
                      value={level}
                      label="Experience Level"
                      onChange={(e) => setLevel(e.target.value)}
                    >
                      <MenuItem value="fresher">Fresher (0–1 yr)</MenuItem>
                      <MenuItem value="mid">Mid (2–5 yrs)</MenuItem>
                      <MenuItem value="experienced">Experienced (6+ yrs)</MenuItem>
                    </Select>
                  </FormControl>
                </>
              )}

              {(interviewToSchedule === 'coding' ||
                (interviewToSchedule === 'technical' && technicalInterviewType === 'AI_TECHNICAL') ||
                (interviewToSchedule === 'hr' && hrInterviewType === 'AI_HR_INTERVIEW')) && (
                <>
                  <TextField
                    label="Time Limit (mins)"
                    type="number"
                    fullWidth
                    required
                    placeholder="Ex: 20 or 30"
                    value={manualQuestion.timeLimit}
                    onChange={(e) => {
                      const raw = parseInt(e.target.value);
                      setManualQuestion({
                        ...manualQuestion,
                        timeLimit: isNaN(raw) ? '' : Math.min(raw, 45)
                      });
                    }}
                    error={manualQuestion.timeLimit > 45}
                    helperText={
                      manualQuestion.timeLimit > 45
                        ? "Maximum allowed duration is 45 minutes."
                        : "Enter the total duration for the AI interview (1 – 45 mins)."
                    }
                    InputProps={{
                      inputProps: { min: 1, max: 45 }
                    }}
                    sx={{
                      "& input::placeholder": { color: "#9e9e9e", opacity: 1 },
                      "& .MuiFormHelperText-root": {
                        color: "#757575",
                        fontSize: "0.85rem",
                        marginLeft: "2px"
                      }
                    }}
                  />
                  <Box sx={{
                    display: 'flex',
                    alignItems: 'flex-start',
                    gap: 1,
                    mt: 0.5,
                    px: 1.5,
                    py: 1.25,
                    bgcolor: '#e3f2fd',
                    border: '1px solid #90caf9',
                    borderRadius: 1.5,
                  }}>
                    <Info sx={{ fontSize: 17, color: '#1565c0', mt: 0.1, flexShrink: 0 }} />
                    <Typography variant="caption" sx={{ color: '#1565c0', lineHeight: 1.6 }}>
                      AI interviews are limited to a maximum of <strong>45 minutes</strong> to ensure an optimal candidate experience and consistent evaluation quality. Duration cannot exceed 45 minutes.
                    </Typography>
                  </Box>
                </>
              )}


{(
  interviewToSchedule === "coding" ||
  (interviewToSchedule === "technical" &&
    technicalInterviewType === "AI_TECHNICAL") ||
  (interviewToSchedule === "hr" &&
    hrInterviewType === "AI_HR_INTERVIEW")
) && (
  <>
    <Box>
      {/* Category Selection Cards */}
      <Box sx={{ display: 'flex', gap: 1.5, mb: 2 }}>
        {[
          {
            value: 'Technical',
            label: 'Technical',
            description: 'Software technologies & frameworks',
          },
          {
            value: 'Non-Technical',
            label: 'Non-Technical (BPO)',
            description: 'Communication, Customer Support, Voice, Non-Voice, Sales, Operations and other BPO skills.',
          },
        ].map(cat => {
          const isSelected = skillCategory === cat.value;
          const isDisabled =
            (cat.value === 'Technical' && isBPO) ||
            (cat.value === 'Non-Technical' && !isBPO);
          return (
            <Box
              key={cat.value}
              onClick={() => {
                if (isDisabled) return;
                if (skillCategory !== cat.value) {
                  setSkillCategory(cat.value);
                  setPrimarySkills([]);
                  setSkillSearch('');
                }
              }}
              sx={{
                flex: 1,
                border: '1.5px solid #0F2D52',
                borderRadius: '6px',
                p: 1.5,
                cursor: isDisabled ? 'default' : 'pointer',
                opacity: isDisabled ? 0.38 : 1,
                bgcolor: isSelected ? '#0F2D52' : '#FFFFFF',
                boxShadow: isSelected ? '0 6px 18px rgba(15,45,82,0.18)' : 'none',
                transition: 'all 0.2s ease',
                ...(!isDisabled && !isSelected && {
                  '&:hover': { bgcolor: '#EEF4FB', borderColor: '#0F2D52' },
                }),
              }}
            >
              <Typography
                sx={{
                  fontWeight: 600,
                  fontSize: '0.875rem',
                  color: isSelected ? '#FFFFFF' : '#0F2D52',
                  mb: 0.25,
                  lineHeight: 1.4,
                }}
              >
                {cat.label}
              </Typography>
              <Typography
                variant="caption"
                sx={{
                  color: isSelected ? 'rgba(255,255,255,0.85)' : '#5F6F85',
                  lineHeight: 1.4,
                  display: 'block',
                }}
              >
                {cat.description}
              </Typography>
            </Box>
          );
        })}
      </Box>

      {/* Label */}
      <Typography sx={{ mb: 1, fontWeight: 600, fontSize: '0.95rem', color: '#1a1a1a' }}>
        Primary Skills
      </Typography>

      {/* Selected skill chips */}
      {primarySkills.length > 0 && (
        <Box
          sx={{
            display: 'flex',
            flexWrap: 'wrap',
            gap: 0.75,
            mb: 1.25,
            px: 1.25,
            py: 0.875,
            bgcolor: '#f8fafc',
            border: '1px solid #e2e8f0',
            borderRadius: 2,
            minHeight: 38,
          }}
        >
          {primarySkills.map(skill => (
            <Chip
              key={skill}
              label={skill}
              size="small"
              onDelete={() => setPrimarySkills(primarySkills.filter(s => s !== skill))}
              sx={{
                bgcolor: '#eff6ff',
                color: '#1d4ed8',
                fontWeight: 500,
                fontSize: '0.78rem',
                border: '1px solid #bfdbfe',
                height: 26,
                '& .MuiChip-deleteIcon': {
                  color: '#93c5fd',
                  fontSize: '16px',
                  '&:hover': { color: '#1d4ed8' },
                },
              }}
            />
          ))}
        </Box>
      )}

      {/* Search input */}
      <TextField
        fullWidth
        placeholder="Search skills..."
        value={skillSearch}
        onChange={(e) => setSkillSearch(e.target.value)}
        InputProps={{
          startAdornment: (
            <InputAdornment position="start">
              <Search sx={{ color: '#94a3b8', fontSize: 20 }} />
            </InputAdornment>
          ),
        }}
        sx={{
          mb: 1.25,
          '& .MuiOutlinedInput-root': {
            borderRadius: 2,
            height: 46,
            '& fieldset': { borderColor: '#e2e8f0' },
            '&:hover fieldset': { borderColor: '#94a3b8' },
            '&.Mui-focused fieldset': { borderColor: '#3b82f6' },
          },
        }}
      />

      {/* Skills list */}
      <Box
        sx={{
          maxHeight: 250,
          overflowY: 'auto',
          border: '1px solid #e2e8f0',
          borderRadius: 2,
          bgcolor: '#fff',
          '&::-webkit-scrollbar': { width: '4px' },
          '&::-webkit-scrollbar-track': { background: '#f1f5f9', borderRadius: '10px' },
          '&::-webkit-scrollbar-thumb': { background: '#cbd5e1', borderRadius: '10px' },
          '&::-webkit-scrollbar-thumb:hover': { background: '#94a3b8' },
        }}
      >
        {currentOptions.length === 0 ? (
          <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', py: 3 }}>
            No technologies added yet. Add technologies to view them here.
          </Typography>
        ) : (
          currentOptions
            .filter(skill => skill.name.toLowerCase().includes(skillSearch.toLowerCase()))
            .map((skill, index, arr) => {
              const isChecked = primarySkills.includes(skill.name);
              return (
                <Box key={skill.name}>
                  <Box
                    onClick={() => {
                      if (isChecked) {
                        setPrimarySkills(primarySkills.filter(s => s !== skill.name));
                      } else {
                        setPrimarySkills([...primarySkills, skill.name]);
                      }
                    }}
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 1.25,
                      px: 1.5,
                      py: 0.875,
                      pl: skill.isSub ? 4.5 : 1.5,
                      cursor: 'pointer',
                      bgcolor: isChecked ? '#eff6ff' : 'transparent',
                      transition: 'background-color 0.15s ease',
                      '&:hover': {
                        bgcolor: isChecked ? '#dbeafe' : '#f8fafc',
                      },
                    }}
                  >
                    {/* Skill avatar */}
                    <Box
                      sx={{
                        width: 28,
                        height: 28,
                        borderRadius: '50%',
                        bgcolor: skill.isSub ? '#f0fdf4' : '#eff6ff',
                        border: `1px solid ${skill.isSub ? '#bbf7d0' : '#bfdbfe'}`,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        flexShrink: 0,
                        fontSize: '0.68rem',
                        fontWeight: 700,
                        color: skill.isSub ? '#16a34a' : '#2563eb',
                        textTransform: 'uppercase',
                        userSelect: 'none',
                      }}
                    >
                      {skill.name.charAt(0)}
                    </Box>

                    {/* Skill name */}
                    <Typography
                      sx={{
                        flex: 1,
                        fontSize: skill.isSub ? '0.84rem' : '0.9rem',
                        fontWeight: skill.isSub ? 400 : 500,
                        color: isChecked ? '#1d4ed8' : (skill.isSub ? '#4b5563' : '#1f2937'),
                        lineHeight: 1.4,
                      }}
                    >
                      {skill.name}
                    </Typography>

                    {/* Subtle parent indicator */}
                    {!skill.isSub && (
                      <ChevronRight sx={{ fontSize: 15, color: '#d1d5db', flexShrink: 0 }} />
                    )}

                    {/* Checkbox */}
                    <Checkbox
                      size="small"
                      checked={isChecked}
                      onChange={(e) => {
                        e.stopPropagation();
                        if (e.target.checked) {
                          setPrimarySkills([...primarySkills, skill.name]);
                        } else {
                          setPrimarySkills(primarySkills.filter(s => s !== skill.name));
                        }
                      }}
                      onClick={(e) => e.stopPropagation()}
                      sx={{
                        p: 0.5,
                        color: '#d1d5db',
                        flexShrink: 0,
                        '&.Mui-checked': { color: '#3b82f6' },
                      }}
                    />
                  </Box>
                  {index < arr.length - 1 && (
                    <Divider sx={{ mx: 1.5, borderColor: '#f8fafc' }} />
                  )}
                </Box>
              );
            })
        )}
      </Box>
    </Box>
  </>
)}

              {interviewToSchedule === 'coding' && codingTestType === 'AI-Manual' && (
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>

                  <Typography variant="subtitle2" fontWeight="bold">
                    Coding Questions
                  </Typography>

                  {questions.map((q, index) => (
                    <Box key={index} sx={{ p: 2, border: '1px solid #eee', borderRadius: 2, mb: 1 }}>
                      <Typography variant="body2" sx={{ mb: 1, fontWeight: 600 }}>Question {index + 1}</Typography>
                      <TextField
                        label={`Question Text`}
                        value={q.question}
                        onChange={(e) => {
                          const updated = [...questions];
                          updated[index] = { ...updated[index], question: e.target.value || "" };
                          setQuestions(updated);
                        }}
                        fullWidth
                        multiline
                        rows={2}
                        sx={{ mb: 2 }}
                      />

                      {q.visibleTestCases.map((tc, tcIdx) => (
                        <Box key={tcIdx} sx={{ ml: 2, mb: 2, p: 1, bgcolor: '#f9f9f9', borderRadius: 1 }}>
                          <Typography variant="caption" sx={{ fontWeight: 'bold', display: 'block', mb: 1 }}>
                            Visible Test Case {tcIdx + 1}
                          </Typography>
                          <Grid container spacing={2}>
                            <Grid item xs={6}>
                              <TextField
                                label="Input"
                                size="small"
                                fullWidth
                                multiline
                                value={tc.input}
                                onChange={(e) => {
                                  const updated = [...questions];
                                  updated[index].visibleTestCases[tcIdx].input = e.target.value;
                                  setQuestions(updated);
                                }}
                              />
                            </Grid>
                            <Grid item xs={6}>
                              <TextField
                                label="Expected Output"
                                size="small"
                                fullWidth
                                multiline
                                value={tc.expectedOutput}
                                onChange={(e) => {
                                  const updated = [...questions];
                                  updated[index].visibleTestCases[tcIdx].expectedOutput = e.target.value;
                                  setQuestions(updated);
                                }}
                              />
                            </Grid>
                          </Grid>
                        </Box>
                      ))}
                    </Box>
                  ))}

                  <Button
                    variant="outlined"
                    onClick={() => setQuestions([...questions, {
                      question: "",
                      visibleTestCases: [
                        { input: "", expectedOutput: "" },
                        { input: "", expectedOutput: "" }
                      ]
                    }])}
                    sx={{ alignSelf: 'flex-start' }}
                  >
                    + Add Question
                  </Button>

                  <Box sx={{ my: 2, borderTop: '1px solid #ddd' }} />


                </Box>
              )}

              {interviewToSchedule === 'coding' && codingTestType === 'AI-Manual' && (
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, p: 2, border: '1px solid #ddd', borderRadius: 1 }}>
                    <Typography variant="subtitle2">Manual Question Details</Typography>
                    <TextField
                        label="Question"
                        multiline
                        rows={3}
                        fullWidth
                        value={manualInterviewQuestions}
                        onChange={(e) => setManualInterviewQuestions(e.target.value)}
                        required
                    />
                    <Grid container spacing={2}>

                      <Grid item xs={6}>
                        <TextField
                            label="Max Score"
                            type="number"
                            fullWidth
                            value={manualQuestion.maxScore}
                            onChange={(e) => setManualQuestion({ ...manualQuestion, maxScore: parseInt(e.target.value) })}
                        />
                      </Grid>
                    </Grid>



                  </Box>
              )}


              {interviewToSchedule !== 'coding' &&
               (interviewToSchedule !== 'technical' || technicalInterviewType === 'TECHNICAL_L2') &&
               !(interviewToSchedule === 'hr' && hrInterviewType === 'AI_HR_INTERVIEW') && (
                  <FormControl fullWidth>
                    <InputLabel>Interview Mode</InputLabel>
                    <Select
                        value={interviewMode}
                        label="Interview Mode"
                        onChange={(e) => {
                          setInterviewMode(e.target.value);

                          if (e.target.value === 'Face-to-Face') {
                            setMeetingLink('');
                          } else if (e.target.value === 'Virtual') {
                            setNotes('');
                          }
                        }}
                    >
                      <MenuItem value="Virtual">Virtual Mode</MenuItem>
                      <MenuItem value="Face-to-Face">Face-to-Face</MenuItem>
                    </Select>
                  </FormControl>
              )}

              {interviewToSchedule !== 'coding' &&
               (interviewToSchedule !== 'technical' || technicalInterviewType === 'TECHNICAL_L2') &&
               !(interviewToSchedule === 'hr' && hrInterviewType === 'AI_HR_INTERVIEW') &&
               interviewMode === 'Face-to-Face' && (
                <TextField
                  label="Interview Location / Instructions"
                  placeholder="Example: Please visit our Hyderabad office at 10 AM. Carry your ID proof."
                  fullWidth
                  multiline
                  rows={4}
                  value={notes}
                  onChange={(e) => setNotes(e.target.value)}
                  required
                  error={interviewMode === 'Face-to-Face' && !notes.trim()}
                  helperText={
                    interviewMode === 'Face-to-Face' && !notes.trim()
                      ? 'Location/instructions are mandatory for face-to-face interviews'
                      : 'Please customize the interview location/instructions so the candidate can understand where to attend.'
                  }
                />
              )}

              {interviewToSchedule !== 'coding' &&
               (interviewToSchedule !== 'technical' || technicalInterviewType === 'TECHNICAL_L2') &&
               !(interviewToSchedule === 'hr' && hrInterviewType === 'AI_HR_INTERVIEW') &&
               interviewMode === 'Virtual' && (
                  <TextField
                      label="Meeting Link"
                      placeholder="e.g. https://zoom.us/j/..."
                      fullWidth
                      value={meetingLink}
                      onChange={(e) => setMeetingLink(e.target.value)}
                      required={interviewMode === 'Virtual'}
                      error={interviewMode === 'Virtual' && !meetingLink}
                      helperText={interviewMode === 'Virtual' && !meetingLink ? "Meeting link is mandatory for virtual mode" : ""}
                  />
              )}

            </Box>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => { setScheduleDialogOpen(false); resetFormState(); }}>Cancel</Button>
            <Button
                onClick={handleScheduleInterview}
                variant="contained"
                disabled={
                    !scheduledDate ||
                    // Technical: must select type
                    (interviewToSchedule === 'technical' && !technicalInterviewType) ||
                    // HR: must select type
                    (interviewToSchedule === 'hr' && !hrInterviewType) ||
                    // Coding: level + timeLimit required
                    (interviewToSchedule === 'coding' && (!level || !manualQuestion.timeLimit)) ||
                    // AI Technical: level + timeLimit + primarySkills required
                    (interviewToSchedule === 'technical' && technicalInterviewType === 'AI_TECHNICAL' && (!level || !manualQuestion.timeLimit || primarySkills.length === 0)) ||
                    // AI HR Interview: level + timeLimit + primarySkills required
                    (interviewToSchedule === 'hr' && hrInterviewType === 'AI_HR_INTERVIEW' && (!level || !manualQuestion.timeLimit || primarySkills.length === 0)) ||
                    // L2/HR L2 virtual: meeting link required
                    (interviewToSchedule !== 'coding' &&
                     (interviewToSchedule !== 'technical' || technicalInterviewType === 'TECHNICAL_L2') &&
                     !(interviewToSchedule === 'hr' && hrInterviewType === 'AI_HR_INTERVIEW') &&
                     interviewMode === 'Virtual' && !meetingLink) ||
                    // L2/HR L2 face-to-face: notes required
                    (interviewToSchedule !== 'coding' &&
                     (interviewToSchedule !== 'technical' || technicalInterviewType === 'TECHNICAL_L2') &&
                     !(interviewToSchedule === 'hr' && hrInterviewType === 'AI_HR_INTERVIEW') &&
                     interviewMode === 'Face-to-Face' && !notes.trim()) ||
                    // Coding AI: primary skills required
                    (interviewToSchedule === 'coding' && (codingTestType === 'AI-Generated' || codingTestType === 'AI-Manual') && primarySkills.length === 0) ||
                    // Coding manual: at least 1 question required
                    (interviewToSchedule === 'coding' &&
                     codingTestType === 'AI-Manual' &&
                     questions.filter(q => q.question && q.question.trim() !== "").length === 0)||
                    // AI interviews: time limit must not exceed 45 minutes
                    ((interviewToSchedule === 'coding' ||
                      (interviewToSchedule === 'technical' && technicalInterviewType === 'AI_TECHNICAL') ||
                      (interviewToSchedule === 'hr' && hrInterviewType === 'AI_HR_INTERVIEW')) &&
                      manualQuestion.timeLimit > 45)
                }
            >
              {isReschedule ? 'Reschedule Interview' : 'Schedule Interview'}
            </Button>
          </DialogActions>
        </Dialog>

        {/* Interview Attempt History Dialog */}
        <Dialog open={historyDialogOpen} onClose={() => setHistoryDialogOpen(false)} maxWidth="sm" fullWidth>
          <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            Reschedule History
            <IconButton onClick={() => setHistoryDialogOpen(false)} size="small"><Close /></IconButton>
          </DialogTitle>
          <DialogContent>
            {attemptHistory.length === 0 ? (
              <Typography variant="body2" color="text.secondary" sx={{ py: 2, textAlign: 'center' }}>
                No scheduling history found.
              </Typography>
            ) : (
              <Box>
                {(() => {
                  const stageLabel = { coding_test: 'AI Coding', ai_technical: 'AI Technical', technical_evaluation: 'Technical Evaluation', hr_behavioral: 'HR & Behavioral' };
                  const grouped = {};
                  attemptHistory.forEach(a => {
                    const key = a.interview_stage;
                    if (!grouped[key]) grouped[key] = [];
                    grouped[key].push(a);
                  });
                  return Object.entries(grouped).map(([stage, attempts]) => (
                    <Box key={stage} sx={{ mb: 3 }}>
                      <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 1, color: '#374151' }}>
                        {stageLabel[stage] || stage}
                      </Typography>
                      {attempts.map((a, idx) => (
                        <Box key={a.id} sx={{
                          display: 'flex', alignItems: 'center', gap: 1.5,
                          p: 1.5, mb: 1, borderRadius: 2,
                          border: '1px solid #e2e8f0',
                          backgroundColor: a.status === 'scheduled' ? '#f0fdf4' : a.status === 'completed' ? '#eff6ff' : '#fafafa'
                        }}>
                          <Box sx={{ flex: 1 }}>
                            <Typography variant="body2" fontWeight={600} sx={{ color: '#1a1a1a' }}>
                              {a.scheduled_at ? new Date(a.scheduled_at).toLocaleString('en-IN', { dateStyle: 'medium', timeStyle: 'short' }) : 'No date'}
                            </Typography>
                          </Box>
                          <Chip
                            label={a.status === 'scheduled' ? 'Active' : a.status === 'rescheduled' ? 'Rescheduled' : a.status === 'completed' ? 'Completed' : a.status}
                            size="small"
                            sx={{
                              fontWeight: 600, fontSize: '0.7rem',
                              bgcolor: a.status === 'scheduled' ? '#dcfce7' : a.status === 'completed' ? '#dbeafe' : '#f3f4f6',
                              color: a.status === 'scheduled' ? '#166534' : a.status === 'completed' ? '#1e40af' : '#6b7280'
                            }}
                          />
                        </Box>
                      ))}
                    </Box>
                  ));
                })()}
              </Box>
            )}
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setHistoryDialogOpen(false)}>Close</Button>
          </DialogActions>
        </Dialog>

        <Dialog open={statusDialogOpen} onClose={() => {
          setStatusDialogOpen(false);

        }}>
          <DialogTitle>
            {pendingStatus === 'shortlisted'
              ? 'Shortlist Candidate'
              : pendingStatus.includes('cancelled')
              ? 'Cancel Interview'
              : 'Reject Candidate'}
          </DialogTitle>
          <DialogContent>
            <Box sx={{ mt: 1 }}>
              <Typography variant="body2" gutterBottom color="text.secondary">
                An email will be sent to the candidate informing them about this update.
                {pendingStatus === 'shortlisted' ? ' They will be invited to proceed to the next stage.' : ''}
              </Typography>
              <TextField
                fullWidth
                multiline
                rows={5}
                value={statusNotes}
                onChange={(e) => setStatusNotes(e.target.value)}
                label={
                  pendingStatus.includes("cancelled")
                    ? "Cancellation Email Content **"
                    : "Additional Notes (Optional)"
                }
                required={pendingStatus.includes("cancelled")}
                error={pendingStatus.includes("cancelled") && !statusNotes.trim()}
                helperText={
                  pendingStatus.includes("cancelled") && !statusNotes.trim()
                    ? "Please provide a reason for cancelling the interview"
                    : ""
                }
              />
            </Box>
          </DialogContent>
          <DialogActions sx={{ p: 2, pt: 0 }}>
            <Button onClick={() => setStatusDialogOpen(false)}>Cancel</Button>
            <Button
              onClick={() => {
                if (pendingStatus.includes("cancelled") && !statusNotes.trim()) {
                  setError("Cancellation reason is required");
                  return;
                }
                handleUpdateStatus();
              }}
              variant="contained"
              color={
                pendingStatus === 'shortlisted'
                  ? 'success'
                  : pendingStatus.includes('cancelled')
                  ? 'warning'
                  : 'error'
              }
              disabled={
                pendingStatus.includes("cancelled") && !statusNotes.trim()
              }
            >
              Confirm {
                pendingStatus === 'shortlisted'
                  ? 'Shortlist'
                  : pendingStatus.includes('cancelled')
                  ? 'Cancel Interview'
                  : 'Reject'
              }
            </Button>
          </DialogActions>
        </Dialog>

        <Dialog
          open={decisionDialogOpen}
          onClose={handleCloseDecisionDialog}
          maxWidth="sm"
          fullWidth
        >
          <DialogTitle>
            {decisionType === "selected" ? "Select Candidate" : "Reject Candidate"}
          </DialogTitle>

          <DialogContent>
            <Typography sx={{ mb: 2 }}>
              An email will be sent to the candidate.
            </Typography>

            <TextField
              fullWidth
              multiline
              rows={5}
              label="Email Content *"
              value={decisionNotes}
              onChange={(e) => setDecisionNotes(e.target.value)}
              required
              error={!decisionNotes.trim()}
              helperText={
                !decisionNotes.trim()
                  ? "Email content is required"
                  : ""
              }
            />
          </DialogContent>

          <DialogActions>
            <Button onClick={handleCloseDecisionDialog}>Cancel</Button>

            <Button
              variant="contained"
              disabled={!decisionNotes.trim()}
              onClick={async () => {
                try {
                  const payload = {
                    status: decisionType === "selected"
                      ? "candidate_selected"
                      : "candidate_rejected",
                    notes: decisionNotes || "No additional comments provided."
                  };

                  console.log("🚀 REJECT PAYLOAD:", payload);

                  await api.patch(`/admin/applications/${id}`, payload);

                  setSuccess("Status updated successfully");
                  setDecisionDialogOpen(false);

                  const res = await api.get(`/applications/${id}`);
                  setApplication(res.data);

                } catch (err) {
                  setError(
                    formatStatus(
                      err.response?.data?.message || "Failed to update status"
                    )
                  );
                  console.error("❌ REJECT ERROR:", err.response?.data || err);
                }
              }}
            >
              Confirm
            </Button>
          </DialogActions>
        </Dialog>
        {showUpgradePopup && (
          <Box sx={{
            position: "fixed",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            background: "white",
            p: 3,
            boxShadow: 5,
            borderRadius: 2,
            zIndex: 999,
            width: { xs: 'calc(100vw - 32px)', sm: 'auto' },
            maxWidth: { xs: '100%', sm: 480 },
          }}>
            <Typography variant="h6">Limit Reached</Typography>
            <Typography sx={{ mt: 1 }}>
              You have reached your subscription plan limit. Please upgrade your plan to continue.
            </Typography>

            <Button
              variant="contained"
              sx={{ mt: 2 }}
              onClick={() => navigate('/pricing')}
            >
              Upgrade Plan
            </Button>

            <Button
              sx={{ mt: 1 }}
              onClick={() => setShowUpgradePopup(false)}
            >
              Cancel
            </Button>
          </Box>
        )}

      </Container>
      <Dialog open={isCodeModalOpen} onClose={() => setIsCodeModalOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          Coding Test Submission
          <IconButton onClick={() => setIsCodeModalOpen(false)} size="small">
            <Close />
          </IconButton>
        </DialogTitle>
        <DialogContent dividers>
          {codingTest ? (
            <Box>
              <Typography variant="subtitle1" gutterBottom fontWeight="bold">
                Selected Language: {codingTest.selected_language || 'Not specified'}
              </Typography>

              <Typography variant="subtitle1" gutterBottom fontWeight="bold" sx={{ mt: 2 }}>
                Submitted Code:
              </Typography>
              <Paper
                variant="outlined"
                sx={{
                  p: 2,
                  bgcolor: '#f5f5f5',
                  fontFamily: 'monospace',
                  whiteSpace: 'pre-wrap',
                  maxHeight: '300px',
                  overflowY: 'auto'
                }}
              >
                {codingTest.candidate_code || 'No code submitted.'}
              </Paper>

              <Typography variant="subtitle1" gutterBottom fontWeight="bold" sx={{ mt: 3 }}>
                Latest Execution Status: {codingTest.execution_status || 'N/A'}
              </Typography>

              {(codingTest.execution_output || codingTest.compile_error || codingTest.runtime_error || codingTest.sql_result) ? (
                <>
                  {codingTest.compile_error && (
                    <Box sx={{ mt: 2 }}>
                      <Typography variant="subtitle2" color="error" gutterBottom>
                        Compile Error:
                      </Typography>
                      <Paper variant="outlined" sx={{ p: 2, bgcolor: '#fff5f5', color: '#d32f2f', fontFamily: 'monospace', whiteSpace: 'pre-wrap' }}>
                        {codingTest.compile_error}
                      </Paper>
                    </Box>
                  )}

                  {codingTest.runtime_error && (
                    <Box sx={{ mt: 2 }}>
                      <Typography variant="subtitle2" color="error" gutterBottom>
                        Runtime Error:
                      </Typography>
                      <Paper variant="outlined" sx={{ p: 2, bgcolor: '#fff5f5', color: '#d32f2f', fontFamily: 'monospace', whiteSpace: 'pre-wrap' }}>
                        {codingTest.runtime_error}
                      </Paper>
                    </Box>
                  )}

                  {codingTest.execution_output && (
                    <Box sx={{ mt: 2 }}>
                      <Typography variant="subtitle2" color="success.main" gutterBottom>
                        Execution Output:
                      </Typography>
                      <Paper variant="outlined" sx={{ p: 2, bgcolor: '#f0fff4', fontFamily: 'monospace', whiteSpace: 'pre-wrap' }}>
                        {codingTest.execution_output}
                      </Paper>
                    </Box>
                  )}

                  {codingTest.sql_result && (
                    <Box sx={{ mt: 2 }}>
                      <Typography variant="subtitle2" color="primary" gutterBottom fontWeight="bold">
                        SQL Output Table:
                      </Typography>
                      {codingTest.sql_result.rows && codingTest.sql_result.rows.length > 0 ? (
                        <TableContainer component={Paper} variant="outlined" sx={{ maxHeight: 300, mb: 1 }}>
                          <Table size="small" stickyHeader>
                            <TableHead>
                              <TableRow>
                                {codingTest.sql_result.columns.map((col) => (
                                  <TableCell key={col} sx={{ fontWeight: 'bold', bgcolor: '#f5f5f5' }}>{col}</TableCell>
                                ))}
                              </TableRow>
                            </TableHead>
                            <TableBody>
                              {codingTest.sql_result.rows.map((row, idx) => (
                                <TableRow key={idx}>
                                  {codingTest.sql_result.columns.map((col) => (
                                    <TableCell key={col}>{row[col]}</TableCell>
                                  ))}
                                </TableRow>
                              ))}
                            </TableBody>
                          </Table>
                        </TableContainer>
                      ) : (
                        <Typography variant="body2" sx={{ mb: 1, fontStyle: 'italic' }}>No rows returned.</Typography>
                      )}

                      <Typography variant="body2" sx={{ mb: 2 }}>
                        <strong>Rows Returned:</strong> {codingTest.sql_result.rowCount || 0}
                      </Typography>

                      {codingTest.sql_result.expectedOutput && (
                        <>
                          <Typography variant="subtitle2" color="text.secondary" gutterBottom fontWeight="bold">
                            Expected Output:
                          </Typography>
                          <Paper variant="outlined" sx={{ p: 1, bgcolor: '#fafafa', mb: 2, fontFamily: 'monospace', fontSize: '0.85rem', whiteSpace: 'pre-wrap' }}>
                            {codingTest.sql_result.expectedOutput}
                          </Paper>
                        </>
                      )}

                      {codingTest.sql_result.matchStatus && (
                        <Box sx={{ mt: 1, display: 'flex', alignItems: 'center', gap: 1 }}>
                          <Typography variant="subtitle2" fontWeight="bold">Output Match:</Typography>
                          <Chip
                            label={codingTest.sql_result.matchStatus}
                            color={codingTest.sql_result.matchStatus === 'PASSED' ? 'success' : 'warning'}
                            size="small"
                          />
                        </Box>
                      )}
                    </Box>
                  )}

                  {/* Task 7: Detailed Test Case Results for General Coding */}
                  {codingTest.test_case_results && Array.isArray(codingTest.test_case_results) && codingTest.test_case_results.length > 0 && (
                    <Box sx={{ mt: 3 }}>
                      <Typography variant="subtitle1" gutterBottom fontWeight="bold" color="primary">
                        Detailed Test Case Results:
                      </Typography>
                      <Stack spacing={2} sx={{ mt: 1 }}>
                        {codingTest.test_case_results.map((tc, idx) => (
                          <Paper key={idx} variant="outlined" sx={{ p: 2, borderRadius: 2 }}>
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                              <Typography variant="subtitle2" fontWeight="bold">
                                Test Case {idx + 1}:
                              </Typography>
                              <Chip
                                label={tc.passed ? "Passed" : "Failed"}
                                color={tc.passed ? "success" : "error"}
                                size="small"
                                variant="outlined"
                              />
                            </Box>

                            <Grid container spacing={2}>
                              <Grid item xs={12} md={6}>
                                <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600 }}>Input:</Typography>
                                <Paper variant="outlined" sx={{ p: 1, bgcolor: '#f8fafc', fontSize: '0.75rem', fontFamily: 'monospace', whiteSpace: 'pre-wrap' }}>
                                  {tc.input || "(no input)"}
                                </Paper>
                              </Grid>
                              <Grid item xs={12} md={6}>
                                <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600 }}>Expected:</Typography>
                                <Paper variant="outlined" sx={{ p: 1, bgcolor: '#f8fafc', fontSize: '0.75rem', fontFamily: 'monospace', whiteSpace: 'pre-wrap' }}>
                                  {tc.expectedOutput}
                                </Paper>
                              </Grid>
                              <Grid item xs={12}>
                                <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600 }}>Actual:</Typography>
                                <Paper variant="outlined" sx={{ p: 1, bgcolor: tc.passed ? '#f0fff4' : '#fff5f5', fontSize: '0.75rem', fontFamily: 'monospace', whiteSpace: 'pre-wrap' }}>
                                  {tc.actualOutput || "(no output)"}
                                </Paper>
                              </Grid>
                            </Grid>
                          </Paper>
                        ))}
                      </Stack>

                      {codingTest.test_case_summary && (
                        <Box sx={{ mt: 2, p: 2, bgcolor: '#f1f5f9', borderRadius: 2 }}>
                          <Typography variant="subtitle2" fontWeight="bold">
                            Summary: {codingTest.test_case_summary.passed} / {codingTest.test_case_summary.total} Passed ({codingTest.test_case_summary.accuracy}%)
                          </Typography>
                        </Box>
                      )}
                    </Box>
                  )}
                </>
              ) : (
                <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                  No execution output available.
                </Typography>
              )}

              {codingTest.executed_at && (
                <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 2 }}>
                  Last executed at: {new Date(codingTest.executed_at).toLocaleString()}
                </Typography>
              )}
            </Box>
          ) : (
            <Typography>No coding test data found.</Typography>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setIsCodeModalOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>
    </>
  );
}

export default ApplicationDetails;
